import {
  prisma,
  reportingPrisma,
  Prisma,
  ParticipantGroupType,
  Profile,
  NetworkingModel,
  User,
  UserRoleAssignmentEntityType,
  InvitationStatus,
  MeetingParticipantType,
  EntityModel,
  ParticipantGroupTranslations,
  ParticipantType,
  NetworkingType,
  NetworkingBehavior,
  ConnectionStatus,
  AvailabilitiesModel,
  AvailabilitiesType,
} from "client";
import jwt from "jsonwebtoken";
import { getLanguages } from "services/language";
import { createSponsor, deleteSponsor } from "services/sponsor";
import { createExhibitor, deleteExhibitor } from "../exhibitor";
import { getBookableItemIdsFromTicketIds } from "services/bookableItem";
import {
  createOrUpdateProfile,
  findExistingProfile,
  setupParticipantFeatures,
} from "services/profile/helper";
import { getBookmarkedEntityIds } from "../bookmark";
import { appendOwnedCompanyHint, resolveMeetingEntityIds } from "../userHub";
import { pickLinkedOwnerInvitation, isParticipantOwner } from "../team/owner";
import { pickPreferredConnectionStatus } from "../connectionRequest/connection-identity.pure";
import { pickCompanyTagNames } from "./companies-tags.pure";
import { normalizeParticipantImportEmail } from "./import.pure";
import {
  invitationRowMatchesRetargetEmails,
  planInvitationEmailRetarget,
} from "./invitation-email-retarget.pure";
import {
  companyDirectoryPeerId,
  directoryMemberUserId,
  isUntouchedInheritedNetworking,
  personalMemberNetworkingDefault,
  shouldListCompanyTeamMember,
} from "./directory-team-member.pure";
import {
  PARTICIPANT_DIRECTORY_PAGE_SIZE,
  PARTICIPANT_GROUP_VIEW_PAGE_SIZE,
  applyDirectoryPages,
  buildDirectoryCursorQuery,
  directoryCursorPageMeta,
  directoryRelationKey,
  EMPTY_DIRECTORY_CURSOR_PAGINATION,
  type DirectoryRelationKey,
} from "./pagination.pure";
import {
  collapseSearchProfiles,
  emptyEveryonePage,
  everyonePageResult,
  hasProfileFieldEquals,
  isAfterUserCursor,
  isIndexedPersonGroupType,
  isObjectIdCursor,
  mergeTypedProfileKeys,
  NETWORK_EVERYONE_MAX_PAGE_SIZE,
  NETWORK_TYPED_PARTICIPANT_MAX_PAGE_SIZE,
  normalizeEveryonePagination,
  normalizeParticipantSearchQuery,
  orderSearchHits,
  participantSearchKeyPipeline,
  pickDirectoryConnectionStatus,
  prismaProfileFieldEquals,
  resolveSearchHits,
  shouldUseIndexedParticipantPage,
  sliceTypedUserPage,
  typedProfileCountPipeline,
  typedProfileKeyPipeline,
  type ProfileFieldEquals,
  type SearchProfileRow,
  type TypedProfileKey,
} from "./everyone-pagination.pure";
import {
  emptyParticipantPage,
  toOidList,
} from "./filter-matches.pure";
import {
  narrowCompaniesByTeamUserIds,
  resolveCompaniesByTeamUserIds,
  resolveExhibitorSponsorTagIds,
  resolveProfileFilterEquals,
  resolveProfileFilterIds,
} from "./filter-ids";
import { pickCompanyTranslation } from "./company-translation.pure";

export {
  getCompaniesByEventWithNetworkData,
  getCompanyByEventAndId,
} from "./companies";
export { withAbsoluteCompanyMedia } from "./companies.pure";
export { parseCompanyListType } from "./companies-type.pure";
export {
  COMPANY_LIST_MAX_PAGE_SIZE,
  isCompanyObjectIdCursor,
} from "./companies-pagination.pure";
export { pickCompanyTranslation } from "./company-translation.pure";

async function ensureBadgeMemberDefaults(eventId: string, userIds: string[]) {
  if (userIds.length === 0) return new Map<string, { type: NetworkingType }[]>();

  const personalDefault = personalMemberNetworkingDefault();
  const [event, networkingRows, availabilityRows] = await Promise.all([
    prisma.event.findUnique({
      where: { id: eventId },
      select: { startDate: true, endDate: true, networkingSettings: true },
    }),
    prisma.networking.findMany({
      where: {
        eventId,
        entityId: { in: userIds },
        entityModel: NetworkingModel.USER,
        behavior: NetworkingBehavior.ALL,
      },
      select: { id: true, entityId: true, type: true, createdAt: true, updatedAt: true },
    }),
    prisma.availability.findMany({
      where: {
        eventId,
        entityId: { in: userIds },
        entityModel: AvailabilitiesModel.USER,
      },
      select: { entityId: true },
    }),
  ]);
  if (!event?.startDate) return new Map<string, { type: NetworkingType }[]>();
  const eventStart = event.startDate;

  const networked = new Set(
    networkingRows
      .map((row) => row.entityId)
      .filter((id): id is string => Boolean(id))
  );
  const available = new Set(
    availabilityRows
      .map((row) => row.entityId)
      .filter((id): id is string => Boolean(id))
  );
  const missingNetworking = userIds.filter((id) => !networked.has(id));
  const missingAvailability = userIds.filter((id) => !available.has(id));
  const inheritedIds = networkingRows
    .filter(
      (row) =>
        row.entityId &&
        isUntouchedInheritedNetworking(
          row.type,
          row.createdAt.getTime(),
          row.updatedAt.getTime()
        )
    )
    .map((row) => row.id);

  // ponytail: availability has no uniqueness constraint; createMany is idempotent
  // for normal reruns, while a concurrent first directory read may duplicate a row.
  await Promise.all([
    missingNetworking.length
      ? Promise.all(
          missingNetworking.map((userId) =>
            prisma.networking
              .create({
                data: {
                  eventId,
                  entityId: userId,
                  entityModel: NetworkingModel.USER,
                  behavior: NetworkingBehavior.ALL,
                  type: personalDefault,
                  createdById: userId,
                },
              })
              .catch(async (error) => {
                // Another request may have inserted the same default first.
                const raced = await prisma.networking.findFirst({
                  where: {
                    eventId,
                    entityId: userId,
                    entityModel: NetworkingModel.USER,
                    behavior: NetworkingBehavior.ALL,
                  },
                });
                if (raced) return raced;
                throw error;
              })
          )
        )
      : Promise.resolve([]),
    inheritedIds.length
      ? prisma.networking.updateMany({
          where: { id: { in: inheritedIds } },
          data: { type: personalDefault },
        })
      : Promise.resolve({ count: 0 }),
    missingAvailability.length
      ? prisma.availability.createMany({
          data: missingAvailability.map((userId) => ({
            eventId,
            entityId: userId,
            entityModel: AvailabilitiesModel.USER,
            type: AvailabilitiesType.EVENT_DAYS,
            startPeriod: eventStart,
            endPeriod: event.endDate ?? eventStart,
            slotDurationInMinutes:
              event.networkingSettings?.meetingSlotDuration ?? 30,
          })),
        })
      : Promise.resolve({ count: 0 }),
  ]);

  const personalRows = await prisma.networking.findMany({
    where: {
      eventId,
      entityId: { in: userIds },
      entityModel: NetworkingModel.USER,
      behavior: NetworkingBehavior.ALL,
    },
    select: { entityId: true, type: true },
  });
  const networkingByUser = new Map<string, { type: NetworkingType }[]>();
  for (const row of personalRows) {
    if (!row.entityId) continue;
    const list = networkingByUser.get(row.entityId) ?? [];
    list.push({ type: row.type });
    networkingByUser.set(row.entityId, list);
  }
  return networkingByUser;
}

export type ParticipantGroupParticipantsPagination = {
  take?: number;
  cursor?: string;
  before?: string;
  includeTotal?: boolean;
};

export type ParticipantGroupParticipantsResult = {
  items: Array<{
    id: string;
    profileId: string | null;
    name: string | null;
    email: string | null;
    phoneNumber: string | null;
    countryCode: string | null;
    qrReferenceNumber: string | null;
    jobTitle: string | null;
    company: string | null;
    country: string | null;
    city: string | null;
    state: string | null;
    gender: string | null;
    age: number | null;
    bio: string | null;
    image: string | null;
    socialMedia: unknown;
    sector?: string | null;
    tags?: string[];
    translations?: Array<{
      name?: string | null;
      bio?: string | null;
      tags?: string[] | null;
      language?: { code?: string | null } | null;
    }>;
    userId: string | null;
    createdAt: Date;
    updatedAt: Date;
  }>;
  pagination: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    nextCursor: string | null;
    prevCursor: string | null;
  };
  total?: number;
};

export const createParticipantGroup = async (
  data: {
    translations: ParticipantGroupTranslations[];
    type: ParticipantGroupType;
    color?: string;
  },
  eventId: string,
  tickets?: string[],
  invitations?: string[]
) => {
  try {
    const bookableItemIds = tickets?.length
      ? await getBookableItemIdsFromTicketIds(tickets)
      : [];
    const participantGroup = await prisma.participantGroup.create({
      data: {
        ...data,
        translations: {
          create: data.translations,
        },
        event: {
          connect: {
            id: eventId,
          },
        },
        ...(bookableItemIds?.length
          ? {
              bookableItems: {
                connect: bookableItemIds.map((id) => ({ id })),
              },
            }
          : {}),
        ...(invitations?.length
          ? {
              invitations: {
                connect: invitations.map((invitation) => ({ id: invitation })),
              },
            }
          : {}),
      },
    });
    return participantGroup;
  } catch (error) {
    console.error(error);
    throw new Error("Failed to create participant group");
  }
};

export const getParticipantGroup = async (id: string, search?: string) => {
  try {
    const participantGroup = await prisma.participantGroup.findUnique({
      where: { id },
      include: {
        participants: {
          where: {
            OR: [
              {
                name: {
                  contains: search,
                  mode: "insensitive",
                },
              },
              {
                email: {
                  contains: search,
                  mode: "insensitive",
                },
              },
            ],
          },
          select: {
            id: true,
            name: true,
            email: true,
            phoneNumber: true,
            countryCode: true,
            qrReferenceNumber: true,
            jobTitle: true,
            company: true,
            country: true,
            city: true,
            state: true,
            gender: true,
            age: true,
            bio: true,
            image: true,
            socialMedia: true,
            userId: true,
            createdAt: true,
            updatedAt: true,
          },
        },
      },
    });
    return participantGroup;
  } catch (error) {
    console.error(error);
    throw new Error("Failed to get participant group");
  }
};

const entityGroupSearchFilter = (search?: string) =>
  search
    ? {
        OR: [
          {
            email: {
              contains: search,
              mode: "insensitive" as const,
            },
          },
          {
            translations: {
              some: {
                name: {
                  contains: search,
                  mode: "insensitive" as const,
                },
              },
            },
          },
        ],
      }
    : {};

const profileGroupSearchFilter = (search?: string) =>
  search
    ? {
        OR: [
          {
            name: {
              contains: search,
              mode: "insensitive" as const,
            },
          },
          {
            email: {
              contains: search,
              mode: "insensitive" as const,
            },
          },
        ],
      }
    : {};

/** Indexed count (participantGroupId / participantGroupIds); same filters as list query. */
const countParticipantGroupMembers = async (
  type: ParticipantGroupType,
  groupId: string,
  search?: string
): Promise<number> => {
  if (type === ParticipantGroupType.EXHIBITORS) {
    return prisma.exhibitor.count({
      where: { participantGroupId: groupId, ...entityGroupSearchFilter(search) },
    });
  }
  if (type === ParticipantGroupType.SPONSORS) {
    return prisma.sponsor.count({
      where: { participantGroupId: groupId, ...entityGroupSearchFilter(search) },
    });
  }
  return prisma.profile.count({
    where: {
      participantGroupIds: { has: groupId },
      ...profileGroupSearchFilter(search),
    },
  });
};

/** Visitor magic links are keyed by User.id; the sheet may pass a Profile.id. */
export const resolveVisitorMagicLinkEntityId = async (
  entityId: string,
  email: string,
  whiteLabelId?: string | null
): Promise<string> => {
  const profile = await prisma.profile.findUnique({
    where: { id: entityId },
    select: { userId: true, email: true },
  });
  if (!profile) return entityId;
  if (profile.userId) return profile.userId;
  if (!whiteLabelId) return entityId;

  const emails = [
    ...new Set(
      [email.toLowerCase(), email, profile.email?.toLowerCase(), profile.email].filter(
        (value): value is string => Boolean(value)
      )
    ),
  ];
  const users = await Promise.all(
    emails.map((candidate) =>
      prisma.user.findUnique({
        where: {
          email_accessibleWhiteLabelId: {
            email: candidate,
            accessibleWhiteLabelId: whiteLabelId,
          },
        },
        select: { id: true },
      })
    )
  );
  return users.find((user) => user?.id)?.id ?? entityId;
};

const COMPANY_TRANSLATION_INCLUDE = {
  include: {
    language: { select: { code: true } },
  },
} as const;

const toClientCompanyTranslations = <
  T extends {
    name: string;
    bio: string | null;
    tags: string[];
    language?: { code: string } | null;
  },
>(
  translations: T[]
) =>
  translations.map((translation) => ({
    name: translation.name,
    bio: translation.bio,
    tags: translation.tags,
    language: translation.language ? { code: translation.language.code } : null,
  }));

export const getParticipantGroupParticipants = async (
  groupId: string,
  search?: string,
  pagination?: ParticipantGroupParticipantsPagination,
  language?: string
): Promise<ParticipantGroupParticipantsResult | null> => {
  try {
    const group = await prisma.participantGroup.findUnique({
      where: { id: groupId },
      select: { type: true, eventId: true },
    });

    if (!group) return null;

    const paginate = pagination !== undefined;
    const take =
      paginate && typeof pagination.take === "number" && pagination.take > 0
        ? pagination.take
        : PARTICIPANT_GROUP_VIEW_PAGE_SIZE;
    const pageQuery = paginate
      ? buildDirectoryCursorQuery(take, pagination.cursor, pagination.before)
      : null;
    const includeTotal = Boolean(paginate && pagination.includeTotal);
    const searchFilter = entityGroupSearchFilter(search);

    if (group.type === ParticipantGroupType.EXHIBITORS) {
      const [exhibitors, total] = await Promise.all([
        prisma.exhibitor.findMany({
          where: {
            participantGroupId: groupId,
            ...searchFilter,
          },
          ...(pageQuery
            ? {
                take: pageQuery.take,
                orderBy: pageQuery.orderBy,
                ...(pageQuery.cursor
                  ? { cursor: pageQuery.cursor, skip: pageQuery.skip }
                  : {}),
              }
            : {}),
          include: {
            translations: COMPANY_TRANSLATION_INCLUDE,
            team: {
              include: {
                profiles: {
                  where: { eventId: group.eventId },
                  take: 1,
                  orderBy: { createdAt: "desc" },
                },
              },
            },
          },
        }),
        includeTotal
          ? countParticipantGroupMembers(group.type, groupId, search)
          : Promise.resolve(undefined),
      ]);

      const mapped = exhibitors.map((exhibitor) => {
        const contactMember =
          exhibitor.team?.find(
            (member) =>
              member.email?.toLowerCase() === exhibitor.email?.toLowerCase()
          ) ?? exhibitor.team?.[0];
        const profile = contactMember?.profiles?.[0];
        const userId = profile?.userId ?? contactMember?.id ?? null;
        const translations = toClientCompanyTranslations(exhibitor.translations);
        const translation = pickCompanyTranslation(translations, language);
        return {
          id: exhibitor.id,
          profileId: profile?.id ?? null,
          name: translation?.name ?? profile?.name ?? null,
          email: exhibitor.email ?? profile?.email ?? null,
          phoneNumber: exhibitor.phone ?? profile?.phoneNumber ?? null,
          website: exhibitor.website,
          facebook: exhibitor.facebook,
          linkedin: exhibitor.linkedin,
          x: exhibitor.x,
          instagram: exhibitor.instagram,
          countryCode: profile?.countryCode ?? null,
          qrReferenceNumber: profile?.qrReferenceNumber ?? null,
          jobTitle: profile?.jobTitle ?? null,
          company: profile?.company ?? null,
          country: profile?.country ?? null,
          sector: profile?.sector ?? null,
          city: profile?.city ?? null,
          state: profile?.state ?? null,
          gender: profile?.gender ?? null,
          age: profile?.age ?? null,
          bio: translation?.bio ?? profile?.bio ?? null,
          image: profile?.image ?? exhibitor.logo ?? null,
          socialMedia: profile?.socialMedia ?? null,
          tags: translation?.tags ?? [],
          translations,
          userId,
          createdAt: exhibitor.createdAt,
          updatedAt: exhibitor.updatedAt,
        };
      });

      if (!paginate) {
        return { items: mapped, pagination: EMPTY_DIRECTORY_CURSOR_PAGINATION };
      }

      const { page, pagination: pageMeta } = directoryCursorPageMeta(
        mapped,
        take,
        {
          cursor: pagination?.cursor,
          before: pagination?.before,
          reverse: pageQuery?.reverse,
        }
      );
      return {
        items: page,
        pagination: pageMeta,
        ...(total !== undefined ? { total } : {}),
      };
    }

    if (group.type === ParticipantGroupType.SPONSORS) {
      const [sponsors, total] = await Promise.all([
        prisma.sponsor.findMany({
          where: {
            participantGroupId: groupId,
            ...searchFilter,
          },
          ...(pageQuery
            ? {
                take: pageQuery.take,
                orderBy: pageQuery.orderBy,
                ...(pageQuery.cursor
                  ? { cursor: pageQuery.cursor, skip: pageQuery.skip }
                  : {}),
              }
            : {}),
          include: {
            translations: COMPANY_TRANSLATION_INCLUDE,
            team: {
              include: {
                profiles: {
                  where: { eventId: group.eventId },
                  take: 1,
                  orderBy: { createdAt: "desc" },
                },
              },
            },
          },
        }),
        includeTotal
          ? countParticipantGroupMembers(group.type, groupId, search)
          : Promise.resolve(undefined),
      ]);

      const mapped = sponsors.map((sponsor) => {
        const contactMember =
          sponsor.team?.find(
            (member) =>
              member.email?.toLowerCase() === sponsor.email?.toLowerCase()
          ) ?? sponsor.team?.[0];
        const profile = contactMember?.profiles?.[0];
        const userId = profile?.userId ?? contactMember?.id ?? null;
        const translations = toClientCompanyTranslations(sponsor.translations);
        const translation = pickCompanyTranslation(translations, language);
        return {
          id: sponsor.id,
          profileId: profile?.id ?? null,
          name: translation?.name ?? profile?.name ?? null,
          email: sponsor.email ?? profile?.email ?? null,
          phoneNumber: sponsor.phone ?? profile?.phoneNumber ?? null,
          website: sponsor.website,
          facebook: sponsor.facebook,
          linkedin: sponsor.linkedin,
          x: sponsor.x,
          instagram: sponsor.instagram,
          countryCode: profile?.countryCode ?? null,
          qrReferenceNumber: profile?.qrReferenceNumber ?? null,
          jobTitle: profile?.jobTitle ?? null,
          company: profile?.company ?? null,
          country: profile?.country ?? null,
          sector: profile?.sector ?? null,
          city: profile?.city ?? null,
          state: profile?.state ?? null,
          gender: profile?.gender ?? null,
          age: profile?.age ?? null,
          bio: translation?.bio ?? profile?.bio ?? null,
          image: profile?.image ?? sponsor.logo ?? null,
          socialMedia: profile?.socialMedia ?? null,
          tags: translation?.tags ?? [],
          translations,
          userId,
          createdAt: sponsor.createdAt,
          updatedAt: sponsor.updatedAt,
        };
      });

      if (!paginate) {
        return { items: mapped, pagination: EMPTY_DIRECTORY_CURSOR_PAGINATION };
      }

      const { page, pagination: pageMeta } = directoryCursorPageMeta(
        mapped,
        take,
        {
          cursor: pagination?.cursor,
          before: pagination?.before,
          reverse: pageQuery?.reverse,
        }
      );
      return {
        items: page,
        pagination: pageMeta,
        ...(total !== undefined ? { total } : {}),
      };
    }

    const participantSearchWhere = profileGroupSearchFilter(search);

    const [participants, total] = await Promise.all([
      prisma.profile.findMany({
        where: {
          participantGroupIds: { has: groupId },
          ...participantSearchWhere,
        },
        ...(pageQuery
          ? {
              take: pageQuery.take,
              orderBy: pageQuery.orderBy,
              ...(pageQuery.cursor
                ? { cursor: pageQuery.cursor, skip: pageQuery.skip }
                : {}),
            }
          : {}),
        select: {
          id: true,
          name: true,
          email: true,
          phoneNumber: true,
          countryCode: true,
          qrReferenceNumber: true,
          jobTitle: true,
          company: true,
          country: true,
          sector: true,
          city: true,
          state: true,
          gender: true,
          age: true,
          bio: true,
          image: true,
          socialMedia: true,
          userId: true,
          createdAt: true,
          updatedAt: true,
        },
      }),
      includeTotal
        ? countParticipantGroupMembers(group.type, groupId, search)
        : Promise.resolve(undefined),
    ]);

    const mapped = participants.map((participant) => ({
      ...participant,
      profileId: participant.id,
    }));

    if (!paginate) {
      return { items: mapped, pagination: EMPTY_DIRECTORY_CURSOR_PAGINATION };
    }

    const { page, pagination: pageMeta } = directoryCursorPageMeta(
      mapped,
      take,
      {
        cursor: pagination?.cursor,
        before: pagination?.before,
        reverse: pageQuery?.reverse,
      }
    );
    return {
      items: page,
      pagination: pageMeta,
      ...(total !== undefined ? { total } : {}),
    };
  } catch (error) {
    console.error(error);
    throw new Error("Failed to get participant group participants");
  }
};

export const getParticipantGroupsByEventId = async (
  eventId: string,
  type?: ParticipantGroupType,
  query?: string,
  session?: User
) => {
  try {
    // if type is not provided, get all participant groups
    // if query is not provided, get all participant groups
    // if both are provided, get participant groups by type and query
    const where: Prisma.ParticipantGroupWhereInput = {
      eventId,
    };
    if (type) {
      where.type = type;
    }
    if (query) {
      where.translations = { some: { name: { contains: query } } };
    }

    const participantGroups = await prisma.participantGroup.findMany({
      where,
      include: {
        translations: true,
        participants: { include: { user: true } },
        exhibitors: { include: { networking: true } },
        sponsors: {
          include: {
            networking: true,
          },
        },
        bookableItems: {
          include: {
            translations: true,
            ticket: true,
          },
        },
        invitations: {
          include: {
            translations: true,
          },
        },
      },
    });

    // Transform bookableItems to tickets to maintain backward compatibility
    return participantGroups.map((group) => ({
      ...group,
      tickets: group.bookableItems
        .map((item) => {
          return { ...item.ticket, translations: item.translations };
        })
        .filter(Boolean),
    }));
  } catch (error) {
    console.error(error);
    throw new Error("Failed to get participant groups");
  }
};

export const getParticipantGroupsByEventIdWithNetworkData = async ({
  eventId,
  type,
  query,
  session,
  userId,
  participantId,
  participantEntityType,
  tag,
  countryCode,
  countryName,
  groupId,
  cursor,
  take,
}: {
  eventId: string;
  type?: ParticipantGroupType;
  query?: string;
  session?: User;
  userId?: string;
  participantId?: string;
  participantEntityType?: ParticipantType;
  tag?: string | undefined;
  countryCode?: string | undefined;
  countryName?: string | undefined;
  groupId?: string;
  cursor?: string;
  take?: number;
}) => {
  try {
    const pageSize =
      typeof take === "number" && take > 0
        ? take
        : PARTICIPANT_DIRECTORY_PAGE_SIZE;
    const where: Prisma.ParticipantGroupWhereInput = { eventId };

    if (type) where.type = type;
    if (groupId) where.id = groupId;
    // if (query) where.translations = { some: { name: { contains: query } } };
    if (userId) where.participants = { some: { userId } };

    let cursorRelation: DirectoryRelationKey | null = null;
    if (cursor && groupId) {
      let cursorGroupType = type;
      if (!cursorGroupType) {
        const group = await prisma.participantGroup.findFirst({
          where: { id: groupId, eventId },
          select: { type: true },
        });
        cursorGroupType = group?.type;
      }
      if (cursorGroupType) {
        cursorRelation = directoryRelationKey(cursorGroupType);
      }
    }

    const memberPageArgs = (relation: DirectoryRelationKey) => ({
      take: pageSize + 1,
      orderBy: { id: "asc" as const },
      ...(cursor && cursorRelation === relation
        ? { cursor: { id: cursor }, skip: 1 }
        : {}),
    });

    const participantGroups = await prisma.participantGroup.findMany({
      where,
      include: {
        translations: {
          include: {
            language: true,
          },
        },
        participants: {
          ...memberPageArgs("participants"),
          where: {
            // Require linked User (orphan profiles out). Keep current user in list;
            // UI hides networking actions on self.
            user: {
              ...(tag ? { tags: { some: { name: tag } } } : {}),
              ...(countryName || countryCode
                ? {
                    OR: [
                      {
                        country: {
                          contains: countryName,
                          mode: "insensitive",
                        },
                      },
                      {
                        country: {
                          contains: countryCode,
                          mode: "insensitive",
                        },
                      },
                    ],
                  }
                : {}),
            },
            ...(query
              ? {
                  OR: [
                    { name: { contains: query, mode: "insensitive" } },
                    { email: { contains: query, mode: "insensitive" } },
                    {
                      user: { name: { contains: query, mode: "insensitive" } },
                    },
                  ],
                }
              : {}),
          },
          include: {
            user: {
              include: {
                networking: true,
                tags: true,
                bookmarks: {
                  where: {
                    entityModel: EntityModel.PROFILE,
                    eventId: eventId,
                    userId: session?.id,
                  },
                },
              },
            },
          },
        },
        exhibitors: {
          ...memberPageArgs("exhibitors"),
          where: {
            ...(query
              ? {
                  OR: [
                    {
                      translations: {
                        some: {
                          name: { contains: query, mode: "insensitive" },
                        },
                      },
                    },
                    { email: { contains: query, mode: "insensitive" } },
                  ],
                }
              : {}),
            ...(tag ? { translations: { some: { tags: { has: tag } } } } : {}),
            ...(countryName || countryCode
              ? {
                  team: {
                    some: {
                      country: { contains: countryName, mode: "insensitive" },
                    },
                  },
                }
              : {}),
          },
          include: {
            networking: true,
            participantGroup: true,
            translations: {
              include: {
                language: true,
              },
            },
            exhibitorInvitations: {
              where: { isOwner: true },
              select: { userId: true, email: true },
              orderBy: { updatedAt: "desc" },
            },
            team: {
              // where: {
              //   OR: [
              //     {
              //       profiles: {
              //         some: {
              //           OR: [
              //             {
              //               eventId: eventId,
              //               exhibitorPermissions: {
              //                 is: { eventDayBadge: true },
              //               },
              //             },
              //           ],
              //         },
              //       },
              //       id: {
              //         not: participantId,
              //       },
              //     },
              //     {
              //       id: session?.id,
              //     },
              //   ],
              // },
              include: {
                networking: { where: { eventId } },
                bookmarks: {
                  where: {
                    entityModel: EntityModel.PROFILE,
                    eventId,
                    userId: session?.id,
                  },
                },
                profiles: {
                  where: {
                    eventId: eventId,
                  },
                },
              },
            },
          },
        },
        sponsors: {
          ...memberPageArgs("sponsors"),
          where: {
            ...(query
              ? {
                  OR: [
                    {
                      translations: {
                        some: {
                          name: { contains: query, mode: "insensitive" },
                        },
                      },
                    },
                    { email: { contains: query, mode: "insensitive" } },
                  ],
                }
              : {}),
            ...(countryName || countryCode
              ? {
                  team: {
                    some: {
                      country: { contains: countryName, mode: "insensitive" },
                    },
                  },
                }
              : {}),
            ...(tag ? { translations: { some: { tags: { has: tag } } } } : {}),
          },
          include: {
            networking: true,
            sponsorInvitations: {
              where: { isOwner: true },
              select: { userId: true, email: true },
              orderBy: { updatedAt: "desc" },
            },
            team: {
              include: {
                networking: { where: { eventId } },
                bookmarks: {
                  where: {
                    entityModel: EntityModel.PROFILE,
                    eventId,
                    userId: session?.id,
                  },
                },
                profiles: {
                  where: { eventId: eventId },
                },
              },
            },
            participantGroup: true,
            translations: {
              include: {
                language: true,
              },
            },
          },
        },
        bookableItems: {
          include: {
            translations: true,
            ticket: true,
          },
        },
        invitations: {
          include: {
            translations: true,
          },
        },
      },
    });

    // ponytail: take+1 window, not count(). A last page of exactly `take` would
    // lie if we compared this batch length to the full filtered total.
    const { groups: pagedGroups, groupsMeta } = applyDirectoryPages(
      participantGroups,
      pageSize
    );

    if (!session) return { groups: pagedGroups, groupsMeta };

    // Connections are stored under session.user.id; company actors also pass
    // participantId. Resolve both so list status matches my-connections/profile.
    const companyHint =
      participantId &&
      (participantEntityType === ParticipantType.EXHIBITOR ||
        participantEntityType === ParticipantType.SPONSOR)
        ? participantId
        : null;
    const networkingEntityIds = await appendOwnedCompanyHint(
      await resolveMeetingEntityIds(session.id, eventId, session.email),
      session.id,
      eventId,
      companyHint,
      session.email
    );

    const companyEntityIds = pagedGroups.flatMap((group) => [
      ...group.exhibitors.map((e) => e.id),
      ...group.sponsors.map((s) => s.id),
    ]);
    const teamUserIds = [
      ...new Set(
        pagedGroups.flatMap((group) => [
          ...group.exhibitors.flatMap((e) => (e.team ?? []).map((m) => m.id)),
          ...group.sponsors.flatMap((s) => (s.team ?? []).map((m) => m.id)),
        ])
      ),
    ];
    const companyOwnerAssignments =
      companyEntityIds.length && teamUserIds.length
        ? await prisma.assignmentModel.findMany({
            where: {
              entityId: { in: companyEntityIds },
              userId: { in: teamUserIds },
              role: {
                name: { in: ["EXHIBITOR_SUPER_ADMIN", "SPONSOR_SUPER_ADMIN"] },
              },
            },
            select: { entityId: true, userId: true },
          })
        : [];
    const companyOwnerKeys = new Set(
      companyOwnerAssignments.map((a) => `${a.entityId}:${a.userId}`)
    );
    const ownerUserIdsByCompany = new Map<string, string[]>();
    const addCompanyOwnerAlias = (companyId: string, userId: string) => {
      const current = ownerUserIdsByCompany.get(companyId) ?? [];
      if (!current.includes(userId)) {
        ownerUserIdsByCompany.set(companyId, [...current, userId]);
      }
    };

    const isDirectoryOwner = (
      entityId: string,
      member: { id: string; email?: string | null },
      companyEmail: string | null,
      ownerInvites: Array<{ userId?: string | null; email?: string | null }>,
      eventProfile?: { email?: string | null }
    ) => {
      if (companyOwnerKeys.has(`${entityId}:${member.id}`)) return true;
      return isParticipantOwner({
        memberUserId: member.id,
        memberEmail: member.email,
        profileEmail: eventProfile?.email,
        ownerInvite: pickLinkedOwnerInvitation(ownerInvites),
        companyEmail,
      });
    };

    for (const assignment of companyOwnerAssignments) {
      if (assignment.entityId) {
        addCompanyOwnerAlias(assignment.entityId, assignment.userId);
      }
    }
    for (const group of pagedGroups) {
      for (const exhibitor of group.exhibitors) {
        for (const member of exhibitor.team ?? []) {
          const eventProfile = member.profiles?.find(
            (profile) => profile.eventId === eventId
          );
          if (
            isDirectoryOwner(
              exhibitor.id,
              member,
              exhibitor.email,
              exhibitor.exhibitorInvitations ?? [],
              eventProfile
            )
          ) {
            addCompanyOwnerAlias(exhibitor.id, member.id);
          }
        }
      }
      for (const sponsor of group.sponsors) {
        for (const member of sponsor.team ?? []) {
          const eventProfile = member.profiles?.find(
            (profile) => profile.eventId === eventId
          );
          if (
            isDirectoryOwner(
              sponsor.id,
              member,
              sponsor.email,
              sponsor.sponsorInvitations ?? [],
              eventProfile
            )
          ) {
            addCompanyOwnerAlias(sponsor.id, member.id);
          }
        }
      }
    }

    // Team members with event-day badge appear as individual directory cards (not owners — company card covers them).
    const participantGroupsWithTeamMembers = pagedGroups.map((group) => {
      const exhibitorBadgeMembers = group.exhibitors.flatMap((e) =>
        (e.team ?? []).flatMap((member) => {
          const eventProfile = member.profiles?.find(
            (p) => p.eventId === eventId
          );
          const isOwner = isDirectoryOwner(
            e.id,
            member,
            e.email,
            e.exhibitorInvitations ?? [],
            eventProfile
          );
          if (
            !shouldListCompanyTeamMember(
              eventProfile?.exhibitorPermissions?.eventDayBadge,
              isOwner
            ) ||
            !eventProfile
          ) {
            return [];
          }
          return [
            {
              ...eventProfile,
              networking: member.networking ?? [],
              translations: [],
              user: {
                ...member,
                name: eventProfile.name ?? member.name,
                networking: member.networking ?? [],
                bookmarks: member.bookmarks ?? [],
              },
              companyMembership: {
                entityId: e.id,
                entityType: ParticipantGroupType.EXHIBITORS,
                translations: e.translations,
              },
            },
          ];
        })
      );
      const sponsorBadgeMembers = group.sponsors.flatMap((s) =>
        (s.team ?? []).flatMap((member) => {
          const eventProfile = member.profiles?.find(
            (p) => p.eventId === eventId
          );
          const isOwner = isDirectoryOwner(
            s.id,
            member,
            s.email,
            s.sponsorInvitations ?? [],
            eventProfile
          );
          if (
            !shouldListCompanyTeamMember(
              eventProfile?.exhibitorPermissions?.eventDayBadge,
              isOwner
            ) ||
            !eventProfile
          ) {
            return [];
          }
          return [
            {
              ...eventProfile,
              networking: member.networking ?? [],
              translations: [],
              user: {
                ...member,
                name: eventProfile.name ?? member.name,
                networking: member.networking ?? [],
                bookmarks: member.bookmarks ?? [],
              },
              companyMembership: {
                entityId: s.id,
                entityType: ParticipantGroupType.SPONSORS,
                translations: s.translations,
              },
            },
          ];
        })
      );

      return {
        ...group,
        exhibitors: [
          ...group.exhibitors.map((company) => ({
            ...company,
            companyMembership: null,
          })),
          ...exhibitorBadgeMembers,
        ],
        sponsors: [
          ...group.sponsors.map((company) => ({
            ...company,
            companyMembership: null,
          })),
          ...sponsorBadgeMembers,
        ],
      };
    });
    const userIds = participantGroupsWithTeamMembers
      .flatMap((group) => [
        ...group.participants.map((p) => p.user?.id),
        ...group.exhibitors.map((e) => directoryMemberUserId(e)),
        ...group.sponsors.map((s) => directoryMemberUserId(s)),
      ])
      .filter((id): id is string => typeof id === "string");

    const exhibitorIds = participantGroupsWithTeamMembers
      .flatMap((group) =>
        group.exhibitors.map((e) => (!e.companyMembership ? e.id : null))
      )
      .filter((id): id is string => typeof id === "string");
    const sponsorIds = participantGroupsWithTeamMembers
      .flatMap((group) =>
        group.sponsors.map((s) => (!s.companyMembership ? s.id : null))
      )
      .filter((id): id is string => typeof id === "string");

    const badgeMemberUserIds = [
      ...new Set(
        participantGroupsWithTeamMembers.flatMap((group) => [
          ...group.exhibitors.map((entry) => directoryMemberUserId(entry)),
          ...group.sponsors.map((entry) => directoryMemberUserId(entry)),
        ]).filter((id): id is string => typeof id === "string")
      ),
    ];
    const personalNetworkingByUser = await ensureBadgeMemberDefaults(
      eventId,
      badgeMemberUserIds
    );
    const withPersonalNetworking = participantGroupsWithTeamMembers.map(
      (group) => ({
        ...group,
        exhibitors: group.exhibitors.map((entry) => {
          const userId = directoryMemberUserId(entry);
          const personal = userId
            ? personalNetworkingByUser.get(userId)
            : undefined;
          if (!personal) return entry;
          const member = entry as typeof entry & {
            user?: { networking?: unknown[] } | null;
          };
          return {
            ...member,
            networking: personal,
            user: member.user
              ? { ...member.user, networking: personal }
              : member.user,
          };
        }),
        sponsors: group.sponsors.map((entry) => {
          const userId = directoryMemberUserId(entry);
          const personal = userId
            ? personalNetworkingByUser.get(userId)
            : undefined;
          if (!personal) return entry;
          const member = entry as typeof entry & {
            user?: { networking?: unknown[] } | null;
          };
          return {
            ...member,
            networking: personal,
            user: member.user
              ? { ...member.user, networking: personal }
              : member.user,
          };
        }),
      })
    );

    // One indexed lookup for current user's company PROFILE bookmarks (not unscoped bookmarks:true).
    const bookmarkedCompanyIds = await getBookmarkedEntityIds(
      session.id,
      eventId,
      EntityModel.PROFILE,
      [...exhibitorIds, ...sponsorIds]
    );

    const companyOwnerPeerIds = [
      ...new Set([...ownerUserIdsByCompany.values()].flat()),
    ];
    const peerIds = [
      ...networkingEntityIds,
      ...userIds,
      ...exhibitorIds,
      ...sponsorIds,
      ...companyOwnerPeerIds,
    ];

    // Get all connection requests with the current user & these exhibitors
    const connectionRequests = await prisma.connectionRequest.findMany({
      where: {
        eventId,
        status: { not: ConnectionStatus.REJECTED },
        OR: [
          {
            senderId: { in: networkingEntityIds },
            receiverId: { in: peerIds },
          },
          {
            receiverId: { in: networkingEntityIds },
            senderId: { in: peerIds },
          },
        ],
      },
      orderBy: { createdAt: "desc" },
    });

    const connectionRequestMap = new Map<string, ConnectionStatus>();
    const setConnectionStatus = (key: string, status: ConnectionStatus) => {
      const prev = connectionRequestMap.get(key);
      if (prev === ConnectionStatus.ACCEPTED) return;
      if (status === ConnectionStatus.ACCEPTED || !prev) {
        connectionRequestMap.set(key, status);
      }
    };
    connectionRequests.forEach((req) => {
      setConnectionStatus(`${req.senderId}|${req.receiverId}`, req.status);
      setConnectionStatus(`${req.receiverId}|${req.senderId}`, req.status);
    });

    const getConnectionRequestType = (
      peerId: string | undefined,
      extraPeerIds: string[] = []
    ) => {
      if (!peerId) return null;
      const statuses: Array<ConnectionStatus | null> = [];
      for (const myId of networkingEntityIds) {
        for (const id of [peerId, ...extraPeerIds]) {
          statuses.push(connectionRequestMap.get(`${myId}|${id}`) ?? null);
        }
      }
      return pickPreferredConnectionStatus(statuses) as ConnectionStatus | null;
    };

    // Mark the groups with the connection request flag and type
    const enrichedGroups = withPersonalNetworking.map((group) => ({
      ...group,
      participants: group.participants.map((p) => {
        const connectionRequestType = getConnectionRequestType(p.user?.id);
        return {
          ...p,
          tickets: group.bookableItems
            .map((item) => {
              return { ...item.ticket, translations: item.translations };
            })
            .filter(Boolean),
          hasConnectionRequest: connectionRequestType !== null,
          connectionRequestType,
        };
      }),
      exhibitors: group.exhibitors.map((e) => {
        const peerId = companyDirectoryPeerId({
          companyId: e.id,
          memberUserId: directoryMemberUserId(e),
        });
        const member = e as typeof e & {
          user?: { bookmarks?: unknown[] } | null;
        };
        const connectionRequestType = getConnectionRequestType(
          peerId,
          e.companyMembership ? [] : ownerUserIdsByCompany.get(e.id) ?? []
        );
        return {
          ...member,
          bookmarks: member.companyMembership
            ? member.user?.bookmarks ?? []
            : bookmarkedCompanyIds.has(e.id)
            ? [{ id: e.id }]
            : [],
          hasConnectionRequest: connectionRequestType !== null,
          connectionRequestType,
        };
      }),
      sponsors: group.sponsors.map((s) => {
        const peerId = companyDirectoryPeerId({
          companyId: s.id,
          memberUserId: directoryMemberUserId(s),
        });
        const member = s as typeof s & {
          user?: { bookmarks?: unknown[] } | null;
        };
        const connectionRequestType = getConnectionRequestType(
          peerId,
          s.companyMembership ? [] : ownerUserIdsByCompany.get(s.id) ?? []
        );
        return {
          ...member,
          bookmarks: member.companyMembership
            ? member.user?.bookmarks ?? []
            : bookmarkedCompanyIds.has(s.id)
            ? [{ id: s.id }]
            : [],
          hasConnectionRequest: connectionRequestType !== null,
          connectionRequestType,
        };
      }),
    }));
    return { groups: enrichedGroups, groupsMeta };
  } catch (error) {
    console.error(error);
    throw new Error("Failed to get participant groups");
  }
};

export const getParticipantDirectoryTagOptions = async (eventId: string) => {
  const [exhibitorRows, sponsorRows] = await Promise.all([
    prisma.exhibitorTranslations.findMany({
      where: {
        exhibitor: {
          participantGroupId: { not: null },
          participantGroup: { eventId },
        },
      },
      select: { tags: true },
    }),
    prisma.sponsorTranslations.findMany({
      where: {
        sponsor: { participantGroup: { eventId } },
      },
      select: { tags: true },
    }),
  ]);

  const names = new Set<string>();
  for (const row of exhibitorRows) {
    for (const tag of row.tags) {
      if (tag?.trim()) names.add(tag.trim());
    }
  }
  for (const row of sponsorRows) {
    for (const tag of row.tags) {
      if (tag?.trim()) names.add(tag.trim());
    }
  }

  return [...names].map((name) => ({ label: name, value: name }));
};

const companyTagStages = (
  entityOids: Array<{ $oid: string }>,
  translationsCollection: "ExhibitorTranslations" | "SponsorTranslations",
  foreignField: "exhibitorId" | "sponsorId"
) => [
  {
    $match: {
      "participant._id": { $in: entityOids },
    },
  },
  {
    $lookup: {
      from: translationsCollection,
      localField: "participant._id",
      foreignField,
      pipeline: [{ $project: { tags: 1, _id: 0 } }],
      as: "tagTranslations",
    },
  },
  {
    $addFields: {
      tags: {
        $ifNull: [{ $arrayElemAt: ["$tagTranslations.tags", 0] }, []],
      },
    },
  },
];

const toMongoJsonId = (id: string) => ({ $oid: id });
const toMongoJsonDate = (date: Date | null) =>
  date ? { $date: date.toISOString() } : null;

const directoryProfileSelect = {
  id: true,
  name: true,
  email: true,
  phoneNumber: true,
  countryCode: true,
  jobTitle: true,
  company: true,
  country: true,
  city: true,
  state: true,
  gender: true,
  age: true,
  bio: true,
  image: true,
  socialMedia: true,
  userId: true,
  createdAt: true,
  updatedAt: true,
  participantGroupIds: true,
  user: {
    select: {
      id: true,
      name: true,
      email: true,
      status: true,
      isAcceptMeetings: true,
      createdAt: true,
      updatedAt: true,
      lastLoginAt: true,
      active: true,
    },
  },
} as const;

function rawMongoId(value: unknown): string | null {
  if (typeof value === "string" && value) return value;
  if (value && typeof value === "object" && "$oid" in value) {
    const oid = (value as { $oid?: unknown }).$oid;
    return typeof oid === "string" ? oid : null;
  }
  return null;
}

function rawMongoDate(value: unknown): Date | null {
  if (value instanceof Date) return value;
  if (value && typeof value === "object" && "$date" in value) {
    const date = (value as { $date?: unknown }).$date;
    if (typeof date === "string" || typeof date === "number") {
      const parsed = new Date(date);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
  }
  return null;
}

async function readTypedProfileKeyBatch(input: {
  eventId: string;
  groupIds: string[];
  limit: number;
  afterUserId?: string;
  onlyUserId?: string;
  fieldEquals?: ProfileFieldEquals | null;
}): Promise<TypedProfileKey[]> {
  const raw = (await reportingPrisma.profile.aggregateRaw({
    pipeline: typedProfileKeyPipeline(input),
  })) as unknown as unknown[];
  const keys: TypedProfileKey[] = [];
  for (const row of raw) {
    if (!row || typeof row !== "object") continue;
    const record = row as {
      _id?: unknown;
      userId?: unknown;
      createdAt?: unknown;
    };
    const id = rawMongoId(record._id);
    const userId = rawMongoId(record.userId);
    if (!id || !userId) continue;
    keys.push({ id, userId, createdAt: rawMongoDate(record.createdAt) });
  }
  return keys;
}

/**
 * Distinct users in userId order. The batch is an index range on the group,
 * not a Prisma `$expr` scan of every profile in the event.
 */
async function readTypedUserWindow(input: {
  eventId: string;
  groupIds: string[];
  take: number;
  skipUsers: number;
  afterUserId?: string;
  fieldEquals?: ProfileFieldEquals | null;
}) {
  const want = input.skipUsers + input.take + 1;
  let after = input.afterUserId;
  let users: TypedProfileKey[] = [];
  for (let guard = 0; users.length < want && guard < 8; guard++) {
    const limit = Math.max(want - users.length + 1, 1);
    const batch = await readTypedProfileKeyBatch({
      eventId: input.eventId,
      groupIds: input.groupIds,
      limit,
      afterUserId: after,
      fieldEquals: input.fieldEquals,
    });
    if (!batch.length) break;
    users = mergeTypedProfileKeys(users, batch);
    const boundary = batch[batch.length - 1]?.userId;
    if (batch.length < limit || !boundary) break;
    const rest = await readTypedProfileKeyBatch({
      eventId: input.eventId,
      groupIds: input.groupIds,
      limit: 50,
      onlyUserId: boundary,
      fieldEquals: input.fieldEquals,
    });
    users = mergeTypedProfileKeys(users, rest);
    after = boundary;
  }
  return sliceTypedUserPage(users, input.skipUsers, input.take);
}

const USER_SEARCH_CHUNK = 500;

async function readParticipantSearchProfiles(input: {
  eventId: string;
  groupIds: string[];
  fieldEquals?: ProfileFieldEquals | null;
}): Promise<SearchProfileRow[]> {
  const raw = (await reportingPrisma.profile.aggregateRaw({
    pipeline: participantSearchKeyPipeline(input),
  })) as unknown as unknown[];
  const rows: SearchProfileRow[] = [];
  for (const row of raw) {
    if (!row || typeof row !== "object") continue;
    const record = row as {
      _id?: unknown;
      userId?: unknown;
      createdAt?: unknown;
      name?: unknown;
    };
    const id = rawMongoId(record._id);
    const userId = rawMongoId(record.userId);
    if (!id || !userId) continue;
    rows.push({
      id,
      userId,
      createdAt: rawMongoDate(record.createdAt),
      name: typeof record.name === "string" ? record.name : null,
    });
  }
  return rows;
}

async function loadUserSearchFields(ids: string[]) {
  const unique = [...new Set(ids)];
  const usersById = new Map<string, { name: string | null; email: string }>();
  if (!unique.length) return usersById;

  const chunks: string[][] = [];
  for (let index = 0; index < unique.length; index += USER_SEARCH_CHUNK) {
    chunks.push(unique.slice(index, index + USER_SEARCH_CHUNK));
  }
  const parts = await Promise.all(
    chunks.map((chunk) =>
      reportingPrisma.user.findMany({
        where: { id: { in: chunk } },
        select: { id: true, name: true, email: true },
      })
    )
  );
  for (const users of parts) {
    for (const user of users) {
      usersById.set(user.id, { name: user.name, email: user.email });
    }
  }
  return usersById;
}

/**
 * Text search over one event's person groups.
 *
 * The match is the typed-page index prefix. Name and email are filtered in
 * process so Mongo never plans a `$regex` or a blocking sort of the group.
 * User name/email are primary-key reads, and only for profiles whose name
 * missed. The page is hydrated by `_id` through the same mapper as Everyone.
 */
async function loadParticipantSearchPage({
  eventId,
  groupIds,
  search,
  take,
  page,
  skipUsers,
  afterUserId,
  fieldEquals,
}: {
  eventId: string;
  groupIds: string[];
  search: string;
  take: number;
  page: number;
  skipUsers: number;
  afterUserId?: string;
  fieldEquals?: ProfileFieldEquals | null;
}) {
  const profiles = await readParticipantSearchProfiles({
    eventId,
    groupIds,
    fieldEquals,
  });
  const collapsed = collapseSearchProfiles(profiles, search);
  const usersById = await loadUserSearchFields(
    collapsed.filter((hit) => hit.needsUserLookup).map((hit) => hit.userId)
  );
  const hits = orderSearchHits(resolveSearchHits(collapsed, usersById, search));
  const visible = afterUserId
    ? hits.filter((hit) => isAfterUserCursor(hit.userId, afterUserId))
    : hits;
  const window = sliceTypedUserPage(visible, skipUsers, take);
  const ids = window.page.map((row) => row.id);
  const hydrated = ids.length
    ? await reportingPrisma.profile.findMany({
        where: { id: { in: ids } },
        select: directoryProfileSelect,
      })
    : [];
  const byId = new Map(hydrated.map((row) => [row.id, row]));

  return {
    data: window.page.flatMap((row) => {
      const profile = byId.get(row.id);
      return profile ? [profile] : [];
    }),
    pagination: {
      total: hits.length,
      hasNextPage: window.hasNextPage,
      hasPreviousPage: Boolean(afterUserId) || page > 1,
      currentPage: page,
      nextCursor: window.nextCursor,
    },
  };
}

async function loadEveryoneProfilePage({
  pageWhere,
  take,
  page,
  offset,
  totalWhere,
  cursor,
}: {
  pageWhere: Prisma.ProfileWhereInput;
  take: number;
  page: number;
  offset: number;
  totalWhere: Prisma.ProfileWhereInput;
  cursor?: string;
}) {
  const [rows, total] = await Promise.all([
    reportingPrisma.profile.findMany({
      where: pageWhere,
      orderBy: { userId: "asc" },
      take: take + 1,
      skip: offset,
      select: directoryProfileSelect,
    }),
    reportingPrisma.profile.count({ where: totalWhere }),
  ]);
  return everyonePageResult(rows, take, page, total, cursor);
}

async function loadTypedParticipantPage({
  eventId,
  groupIds,
  take,
  page,
  skipUsers,
  afterUserId,
  fieldEquals,
}: {
  eventId: string;
  groupIds: string[];
  take: number;
  page: number;
  skipUsers: number;
  afterUserId?: string;
  fieldEquals?: ProfileFieldEquals | null;
}) {
  const countPromise = reportingPrisma.profile.aggregateRaw({
    pipeline: typedProfileCountPipeline({ eventId, groupIds, fieldEquals }),
  }) as Promise<unknown>;
  const window = await readTypedUserWindow({
    eventId,
    groupIds,
    take,
    skipUsers,
    afterUserId,
    fieldEquals,
  });
  const countRaw = (await countPromise) as Array<{ total?: number }>;
  const ids = window.page.map((row) => row.id);
  const hydrated = ids.length
    ? await reportingPrisma.profile.findMany({
        where: { id: { in: ids } },
        select: directoryProfileSelect,
      })
    : [];
  const byId = new Map(hydrated.map((row) => [row.id, row]));

  return {
    data: window.page.flatMap((row) => {
      const profile = byId.get(row.id);
      return profile ? [profile] : [];
    }),
    pagination: {
      total: Number(countRaw?.[0]?.total ?? 0),
      hasNextPage: window.hasNextPage,
      hasPreviousPage: Boolean(afterUserId) || page > 1,
      currentPage: page,
      nextCursor: window.nextCursor,
    },
  };
}

/**
 * Fast path for the default Network "everyone" tab, person lists
 * (VISITORS, SPEAKERS, MEDIA, SUPPORT, OTHER), text search, and profile field
 * filters (`country` / `company` / `jobTitle`) on those lists.
 *
 * The legacy aggregation starts at ParticipantGroup, expands every profile and
 * user in the group, then paginates at the end. Starting from the indexed
 * Profile collection lets Mongo apply the page window before any per-user
 * networking work. Reads stay on the reporting Prisma pool.
 *
 * Everyone uses Profile(eventId, userId) findMany. That list is dense, so one
 * index range fills a page. Typed lists do not. The same Prisma find compiles
 * to `$expr` and, for a sparse type such as speakers, walks every profile in
 * the event. Typed pages use a plain `$match` on
 * Profile(eventId, active, participantGroupIds, userId), keep one profile per
 * user in userId order, then hydrate that page by primary key. Text search
 * uses that same match for the caller's groups, then filters name and email
 * in process. Profile field filters add equality `$in` on the reporting
 * country/company/jobTitle indexes. Everyone stays capped at 50 and typed
 * lists at 100.
 */
async function getEveryoneParticipantsPage({
  eventId,
  lang,
  session,
  page,
  take,
  cursor,
  includeCompanyTeamMembers,
  groupType,
  search,
  fieldEquals,
}: {
  eventId: string;
  lang: string;
  session?: User;
  page: number;
  take: number;
  cursor?: string;
  includeCompanyTeamMembers: boolean;
  groupType?: ParticipantGroupType;
  search?: string;
  fieldEquals?: ProfileFieldEquals | null;
}) {
  const pagination = normalizeEveryonePagination(
    page,
    take,
    groupType
      ? NETWORK_TYPED_PARTICIPANT_MAX_PAGE_SIZE
      : NETWORK_EVERYONE_MAX_PAGE_SIZE
  );
  const safeCursor = isObjectIdCursor(cursor) ? cursor : undefined;

  const groups = await reportingPrisma.participantGroup.findMany({
    where: {
      eventId,
      ...(groupType
        ? { type: groupType }
        : !includeCompanyTeamMembers
          ? {
              type: {
                notIn: [
                  ParticipantGroupType.EXHIBITORS,
                  ParticipantGroupType.SPONSORS,
                ],
              },
            }
          : {}),
    },
    select: {
      id: true,
      type: true,
      color: true,
      translations: {
        where: { language: { code: lang } },
        select: { name: true },
      },
    },
    orderBy: { id: "asc" },
  });

  if (!groups.length) return emptyEveryonePage(pagination.page);

  const groupIds = groups.map((group) => group.id);
  const fieldWhere = prismaProfileFieldEquals(fieldEquals);
  const where: Prisma.ProfileWhereInput = {
    eventId,
    // The old pipeline matched `$ne false`, which also accepted documents with
    // no `active` field. Prisma guards required fields with `$ne $$REMOVE`, so
    // no filter spelling reproduces that; `active` is written on every create.
    active: true,
    userId: { not: null },
    participantGroupIds: { hasSome: groupIds },
    ...fieldWhere,
  };
  const pageWhere: Prisma.ProfileWhereInput = safeCursor
    ? { ...where, userId: { gt: safeCursor } }
    : where;
  const offset = safeCursor ? 0 : (pagination.page - 1) * pagination.take;

  // Everyone stays on the Profile(eventId, userId) find. That list is dense, so
  // a single-key userId order fills a page without a blocking sort. Typed lists
  // cannot use the same Prisma find: it compiles to `$expr`, which walks every
  // profile in the event before it reaches a small group such as speakers.
  // Search and profile field filters reuse the typed match for these same group
  // ids, so a speaker query stays on the speaker groups instead of `$facet`.
  const rawPage = search
    ? await loadParticipantSearchPage({
        eventId,
        groupIds,
        search,
        take: pagination.take,
        page: pagination.page,
        skipUsers: offset,
        afterUserId: safeCursor,
        fieldEquals,
      })
    : groupType || hasProfileFieldEquals(fieldEquals)
      ? await loadTypedParticipantPage({
          eventId,
          groupIds,
          take: pagination.take,
          page: pagination.page,
          skipUsers: offset,
          afterUserId: safeCursor,
          fieldEquals,
        })
      : await loadEveryoneProfilePage({
          pageWhere,
          take: pagination.take,
          page: pagination.page,
          offset,
          totalWhere: where,
          cursor: safeCursor,
        });
  const peerIds = rawPage.data
    .map((row) => row.user?.id)
    .filter((id): id is string => Boolean(id));

  const connectionStatusByUser = new Map<string, ConnectionStatus>();
  const bookmarkedUserIds = new Set<string>();
  const networkingByUser = new Map<
    string,
    Awaited<ReturnType<typeof reportingPrisma.networking.findMany>>[number]
  >();

  if (session && peerIds.length) {
    // Connection and networking rows stay unscoped by event, matching the
    // lookups the legacy pipeline ran; scoping them here would silently drop
    // states the app already renders.
    const [connectionRequests, bookmarks, networkingRows] = await Promise.all([
      reportingPrisma.connectionRequest.findMany({
        where: {
          OR: [
            { senderId: session.id, receiverId: { in: peerIds } },
            { receiverId: session.id, senderId: { in: peerIds } },
          ],
        },
        select: {
          senderId: true,
          receiverId: true,
          status: true,
        },
      }),
      reportingPrisma.bookmark.findMany({
        where: {
          userId: session.id,
          eventId,
          entityModel: EntityModel.PROFILE,
          entityId: { in: peerIds },
        },
        select: { entityId: true },
      }),
      reportingPrisma.networking.findMany({
        where: { entityId: { in: peerIds } },
        orderBy: { createdAt: "desc" },
      }),
    ]);

    const statusesByUser = new Map<string, ConnectionStatus[]>();
    for (const request of connectionRequests) {
      const peerId =
        request.senderId === session.id ? request.receiverId : request.senderId;
      const statuses = statusesByUser.get(peerId) ?? [];
      statuses.push(request.status);
      statusesByUser.set(peerId, statuses);
    }
    for (const [peerId, statuses] of statusesByUser) {
      const preferred = pickDirectoryConnectionStatus(
        statuses
      ) as ConnectionStatus | null;
      if (preferred) connectionStatusByUser.set(peerId, preferred);
    }

    for (const bookmark of bookmarks) bookmarkedUserIds.add(bookmark.entityId);

    for (const networking of networkingRows) {
      if (!networking.entityId) continue;
      const current = networkingByUser.get(networking.entityId);
      // Prefer this event's row; fall back to any row so users configured only
      // at another event keep the setting the legacy lookup returned.
      if (
        !current ||
        (networking.eventId === eventId && current.eventId !== eventId)
      ) {
        networkingByUser.set(networking.entityId, networking);
      }
    }
  }

  const groupRank = new Map(groups.map((group, index) => [group.id, index]));
  return {
    data: rawPage.data.flatMap((row) => {
      if (!row.user) return [];
      // Deterministic group pick: lowest group id, matching the order the old
      // pipeline's `$first` accumulator saw.
      const group = row.participantGroupIds
        .filter((groupId) => groupRank.has(groupId))
        .sort((a, b) => (groupRank.get(a) ?? 0) - (groupRank.get(b) ?? 0))
        .map((groupId) => groups[groupRank.get(groupId) as number])[0];
      if (!group) return [];

      const {
        user,
        participantGroupIds: _participantGroupIds,
        ...profile
      } = row;
      const connectionRequest = session
        ? connectionStatusByUser.get(user.id) ?? null
        : undefined;
      const networking = networkingByUser.get(user.id);
      const rawNetworking = networking
        ? (() => {
            const {
              id,
              eventId: networkingEventId,
              entityId,
              createdById,
              createdAt,
              updatedAt,
              ...fields
            } = networking;
            return {
              ...fields,
              _id: toMongoJsonId(id),
              eventId: toMongoJsonId(networkingEventId),
              entityId: entityId ? toMongoJsonId(entityId) : null,
              createdById: createdById ? toMongoJsonId(createdById) : null,
              createdAt: toMongoJsonDate(createdAt),
              updatedAt: toMongoJsonDate(updatedAt),
            };
          })()
        : null;

      return [
        {
          participant: {
            ...user,
            _id: toMongoJsonId(user.id),
            createdAt: toMongoJsonDate(user.createdAt),
            updatedAt: toMongoJsonDate(user.updatedAt),
            lastLoginAt: toMongoJsonDate(user.lastLoginAt),
          },
          profile: {
            ...profile,
            _id: toMongoJsonId(profile.id),
            userId: profile.userId ? toMongoJsonId(profile.userId) : null,
            createdAt: toMongoJsonDate(profile.createdAt),
            updatedAt: toMongoJsonDate(profile.updatedAt),
          },
          id: user.id,
          // `$group` always emitted a tags key on this listing, null when the
          // suggestion branch never populated it.
          tags: null,
          groupType: group.type,
          groupName: group.translations,
          color: group.color,
          connectionRequests: connectionRequest,
          networking: session ? rawNetworking : undefined,
          isBookmarked: session ? bookmarkedUserIds.has(user.id) : undefined,
        },
      ];
    }),
    pagination: rawPage.pagination,
  };
}

export const getParticipantsByGroupTypeWithNetworkData = async (
  eventId: string,
  type?: ParticipantGroupType,
  lang: string = "en",
  query?: string,
  session?: User,
  page: number = 1,
  take: number = 10,
  tags?: string[],
  jobTitle?: string,
  country?: string,
  suggestion?: boolean,
  myConnections?: boolean,
  participantGroupId?: string,
  company?: string,
  includeCompanyTeamMembers: boolean = true,
  cursor?: string
) => {
  try {
    const safeQuery = normalizeParticipantSearchQuery(query);
    const tagNames = (tags ?? []).map((tag) => tag.trim()).filter(Boolean);
    const countryFilter = country?.trim() || undefined;
    const companyFilter = company?.trim() || undefined;
    const jobTitleFilter = jobTitle?.trim() || undefined;
    const hasTags = tagNames.length > 0;
    const hasProfileFilters = Boolean(
      countryFilter || companyFilter || jobTitleFilter
    );

    if (
      shouldUseIndexedParticipantPage({
        type,
        hasTags,
        suggestion,
        myConnections,
        participantGroupId,
      })
    ) {
      let fieldEquals: ProfileFieldEquals | null | undefined;
      if (hasProfileFilters) {
        fieldEquals = await resolveProfileFilterEquals(eventId, {
          country: countryFilter,
          company: companyFilter,
          jobTitle: jobTitleFilter,
        });
        if (fieldEquals === null) return emptyEveryonePage(page);
      }

      return getEveryoneParticipantsPage({
        eventId,
        lang,
        session,
        page,
        take,
        cursor,
        includeCompanyTeamMembers,
        groupType:
          type && isIndexedPersonGroupType(type)
            ? (type as ParticipantGroupType)
            : undefined,
        search: safeQuery || undefined,
        fieldEquals,
      });
    }

    if (
      hasTags &&
      type &&
      type !== ParticipantGroupType.EXHIBITORS &&
      type !== ParticipantGroupType.SPONSORS
    ) {
      return emptyParticipantPage(page);
    }

    let profileOids: Array<{ $oid: string }> | undefined;
    let profileUserIds: string[] = [];
    if (hasProfileFilters) {
      const resolvedProfiles = await resolveProfileFilterIds(eventId, {
        country: countryFilter,
        company: companyFilter,
        jobTitle: jobTitleFilter,
      });
      if (!resolvedProfiles.profileIds.length) return emptyParticipantPage(page);
      profileOids = toOidList(resolvedProfiles.profileIds);
      profileUserIds = resolvedProfiles.userIds;
    }

    let exhibitorIds: string[] = [];
    let sponsorIds: string[] = [];
    if (hasTags) {
      const tagged = await resolveExhibitorSponsorTagIds(
        eventId,
        tagNames,
        type
      );
      exhibitorIds = tagged.exhibitorIds;
      sponsorIds = tagged.sponsorIds;
      if (!exhibitorIds.length && !sponsorIds.length) {
        return emptyParticipantPage(page);
      }
    }

    const restrictCompaniesByTeam =
      hasProfileFilters &&
      (hasTags ||
        type === ParticipantGroupType.EXHIBITORS ||
        type === ParticipantGroupType.SPONSORS);

    if (restrictCompaniesByTeam) {
      const narrowed = hasTags
        ? await narrowCompaniesByTeamUserIds(eventId, profileUserIds, {
            exhibitorIds,
            sponsorIds,
          })
        : await resolveCompaniesByTeamUserIds(eventId, profileUserIds, type);
      exhibitorIds = narrowed.exhibitorIds;
      sponsorIds = narrowed.sponsorIds;
      if (!exhibitorIds.length && !sponsorIds.length) {
        return emptyParticipantPage(page);
      }
    }

    const exhibitorOids = toOidList(exhibitorIds);
    const sponsorOids = toOidList(sponsorIds);
    const hasCompanyIdFilter = exhibitorOids.length + sponsorOids.length > 0;

    // Suggestions, my-connections, a single participantGroupId, tags on
    // company types, and company types themselves still use this aggregation.
    // Person text search and profile field filters do not.
    // Delete it once those callers have their own indexed page queries.
    let lookupStage: any[];
    const groupTypeMatch = hasTags && !type
      ? {
          type: {
            $in: [
              ParticipantGroupType.EXHIBITORS,
              ParticipantGroupType.SPONSORS,
            ],
          },
        }
      : type
        ? { type }
        : includeCompanyTeamMembers
          ? {}
          : {
              type: {
                $nin: [
                  ParticipantGroupType.EXHIBITORS,
                  ParticipantGroupType.SPONSORS,
                ],
              },
            };
    switch (type) {
      case ParticipantGroupType.EXHIBITORS:
        lookupStage = [
          {
            $lookup: {
              from: "Exhibitor",
              localField: "_id",
              foreignField: "participantGroupId",
              as: "participant",
            },
          },
          {
            $unwind: "$participant",
          },
          ...(hasCompanyIdFilter && exhibitorOids.length
            ? companyTagStages(exhibitorOids, "ExhibitorTranslations", "exhibitorId")
            : []),
        ];
        break;
      case ParticipantGroupType.SPONSORS:
        lookupStage = [
          {
            $lookup: {
              from: "Sponsor",
              localField: "_id",
              foreignField: "participantGroupId",
              as: "participant",
            },
          },
          {
            $unwind: "$participant",
          },
          ...(hasCompanyIdFilter && sponsorOids.length
            ? companyTagStages(sponsorOids, "SponsorTranslations", "sponsorId")
            : []),
        ];
        break;
      default:
        if (hasTags) {
          lookupStage = [
            {
              $lookup: {
                from: "Exhibitor",
                localField: "_id",
                foreignField: "participantGroupId",
                as: "exhibitorsLookup",
              },
            },
            {
              $lookup: {
                from: "Sponsor",
                localField: "_id",
                foreignField: "participantGroupId",
                as: "sponsorsLookup",
              },
            },
            {
              $addFields: {
                participant: {
                  $cond: [
                    { $eq: ["$type", ParticipantGroupType.EXHIBITORS] },
                    { $ifNull: ["$exhibitorsLookup", []] },
                    { $ifNull: ["$sponsorsLookup", []] },
                  ],
                },
              },
            },
            { $unwind: "$participant" },
            {
              $match: {
                "participant._id": { $in: [...exhibitorOids, ...sponsorOids] },
              },
            },
            {
              $lookup: {
                from: "ExhibitorTranslations",
                localField: "participant._id",
                foreignField: "exhibitorId",
                pipeline: [{ $project: { tags: 1, _id: 0 } }],
                as: "exhibitorTagTranslations",
              },
            },
            {
              $lookup: {
                from: "SponsorTranslations",
                localField: "participant._id",
                foreignField: "sponsorId",
                pipeline: [{ $project: { tags: 1, _id: 0 } }],
                as: "sponsorTagTranslations",
              },
            },
            {
              $addFields: {
                tags: {
                  $ifNull: [
                    {
                      $arrayElemAt: [
                        {
                          $cond: [
                            {
                              $gt: [{ $size: "$exhibitorTagTranslations" }, 0],
                            },
                            "$exhibitorTagTranslations.tags",
                            "$sponsorTagTranslations.tags",
                          ],
                        },
                        0,
                      ],
                    },
                    [],
                  ],
                },
              },
            },
          ];
          break;
        }
        lookupStage = [
          {
            $lookup: {
              from: "Profile",
              // Performance: join Profiles using the group’s `participantIds` (primary keys)
              // instead of scanning Profiles and checking membership in `participantGroupIds`.
              // This turns the join into an `_id IN [...]` match which is typically far cheaper.
              let: { participantIds: { $ifNull: ["$participantIds", []] } },
              pipeline: [
                {
                  $match: {
                    ...(profileOids?.length ? { _id: { $in: profileOids } } : {}),
                    $expr: {
                      $and: [
                        {
                          // Guard: if the group has no participants, skip the lookup quickly
                          // (avoids unnecessary work and ensures `$in` isn’t evaluated on empty input).
                          $gt: [{ $size: "$$participantIds" }, 0],
                        },
                        { $in: ["$_id", "$$participantIds"] },
                        { $eq: ["$eventId", { $oid: eventId }] },
                        { $ne: ["$active", false] },
                      ],
                    },
                  },
                },
                { $sort: { createdAt: -1 } },
                {
                  $project: {
                    // Performance: limit payload to fields required downstream / response,
                    // reducing memory and network costs through the rest of the pipeline.
                    _id: 1,
                    id: { $toString: "$_id" },
                    name: 1,
                    email: 1,
                    phoneNumber: 1,
                    countryCode: 1,
                    jobTitle: 1,
                    company: 1,
                    country: 1,
                    city: 1,
                    state: 1,
                    gender: 1,
                    age: 1,
                    bio: 1,
                    image: 1,
                    socialMedia: 1,
                    userId: 1,
                    createdAt: 1,
                    updatedAt: 1,
                  },
                },
              ],
              as: "profiles",
            },
          },

          {
            $unwind: { path: "$profiles", preserveNullAndEmptyArrays: true },
          },

          {
            $lookup: {
              from: "User",
              // Performance: pipeline lookup + projection avoids pulling the full User document
              // when only a subset of fields is needed downstream (and preserves `tagIds` for Tag lookup).
              let: { userId: "$profiles.userId" },
              pipeline: [
                {
                  $match: {
                    $expr: { $eq: ["$_id", "$$userId"] },
                  },
                },
                {
                  $project: {
                    // Keep fields consumed by later stages + response projection.
                    _id: 1,
                    id: { $toString: "$_id" },
                    name: 1,
                    email: 1,
                    status: 1,
                    isAcceptMeetings: 1,
                    createdAt: 1,
                    updatedAt: 1,
                    lastLoginAt: 1,
                    active: 1,
                    tagIds: 1,
                  },
                },
              ],
              as: "participant",
            },
          },
          {
            $unwind: "$participant",
          },
          ...(safeQuery
            ? [
                {
                  $match: {
                    $or: [
                      {
                        "participant.name": {
                          $regex: safeQuery,
                          $options: "im",
                        },
                      },
                      {
                        "profiles.name": {
                          $regex: safeQuery,
                          $options: "im",
                        },
                      },
                      {
                        "participant.email": {
                          $regex: safeQuery,
                          $options: "im",
                        },
                      },
                    ],
                  },
                },
              ]
            : []),
          ...(suggestion
            ? [
                {
                  $lookup: {
                    from: "Tag",
                    let: {
                      tagIds: { $ifNull: ["$participant.tagIds", []] },
                      eventId: { $toObjectId: eventId },
                    },
                    pipeline: [
                      {
                        $match: {
                          $expr: {
                            $and: [
                              { $in: ["$_id", "$$tagIds"] },
                              { $eq: ["$eventId", "$$eventId"] },
                            ],
                          },
                        },
                      },
                      { $addFields: { id: { $toString: "$_id" } } },
                    ],
                    as: "tags",
                  },
                },
              ]
            : []),
        ];
        break;
    }

    let connectionRequestLookup: any[] = [];
    if (session) {
      connectionRequestLookup = [
        {
          $lookup: {
            from: "ConnectionRequest",
            let: {
              participantId: "$participant._id",
              currentUserId: { $toObjectId: session.id },
            },
            pipeline: [
              {
                $match: {
                  $expr: {
                    $or: [
                      {
                        $and: [
                          { $eq: ["$senderId", "$$currentUserId"] },
                          { $eq: ["$receiverId", "$$participantId"] },
                        ],
                      },
                      {
                        $and: [
                          { $eq: ["$receiverId", "$$currentUserId"] },
                          { $eq: ["$senderId", "$$participantId"] },
                        ],
                      },
                    ],
                  },
                },
              },
              {
                $project: {
                  _id: 0,
                  status: 1,
                },
              },
            ],
            as: "connectionRequests",
          },
        },
        {
          $addFields: {
            connectionRequests: {
              $cond: [
                { $eq: [{ $size: "$connectionRequests" }, 0] },
                null,
                { $arrayElemAt: ["$connectionRequests.status", 0] },
              ],
            },
          },
        },
        ...(myConnections
          ? [
              {
                $match: {
                  connectionRequests: { $in: ["PENDING", "ACCEPTED"] },
                },
              },
            ]
          : []),
        {
          $lookup: {
            from: "Bookmark",
            let: {
              participantId: "$participant._id",
              currentUserId: { $toObjectId: session.id },
              currentEventId: { $toObjectId: eventId },
            },
            pipeline: [
              {
                $match: {
                  $expr: {
                    $and: [
                      { $eq: ["$entityId", "$$participantId"] },
                      { $eq: ["$userId", "$$currentUserId"] },
                      { $eq: ["$eventId", "$$currentEventId"] },
                      { $eq: ["$entityModel", "PROFILE"] },
                    ],
                  },
                },
              },
            ],
            as: "bookmarks",
          },
        },
        {
          $addFields: {
            isBookmarked: {
              $cond: [{ $eq: [{ $size: "$bookmarks" }, 0] }, false, true],
            },
          },
        },
        {
          $lookup: {
            from: "Networking",
            localField: "participant._id",
            foreignField: "entityId",
            as: "networking",
          },
        },
        {
          $addFields: {
            networking: {
              $cond: [
                { $eq: [{ $size: "$networking" }, 0] },
                null,
                { $arrayElemAt: ["$networking", 0] },
              ],
            },
          },
        },
      ];
      // Current user stays in the list; UI hides self actions.
      if (suggestion) {
        const userTags = await prisma.tag.findMany({
          where: {
            eventId,
            users: {
              some: {
                id: session?.id,
              },
            },
          },
          select: {
            id: true,
          },
        });
        const userTagIds = userTags.map((t) => t.id);
        if (userTagIds.length) {
          lookupStage.push({
            $match: {
              "tags.id": { $in: userTagIds },
            },
          });
        } else {
          // Early return with no results if user has no tags
          return {
            data: [],
            pagination: [{ total: 0, hasNextPage: false }],
          };
        }
      }
    }
    const pipeline = [
      {
        $match: {
          eventId: { $oid: eventId },
          ...groupTypeMatch,
          ...(participantGroupId ? { _id: { $oid: participantGroupId } } : {}),
        },
      },
      ...lookupStage,
      {
        $facet: {
          data: [
            {
              $lookup: {
                from: "ParticipantGroupTranslations",
                localField: "_id",
                foreignField: "participantGroupId",
                as: "translations",
                pipeline: [
                  {
                    $lookup: {
                      from: "Language",
                      localField: "languageId",
                      foreignField: "_id",
                      as: "language",
                    },
                  },
                  {
                    $match: {
                      "language.code": lang,
                    },
                  },
                  {
                    $project: {
                      _id: 0,
                      name: 1,
                    },
                  },
                ],
              },
            },
            {
              $group: {
                _id: "$participant._id",
                participant: { $first: "$participant" },
                profile: { $first: "$profiles" },
                type: { $first: "$type" },
                translations: { $first: "$translations" },
                color: { $first: "$color" },
                tags: { $first: "$tags" },
              },
            },

            { $sort: { "participant._id": 1 } },
            { $skip: (page - 1) * take },
            { $limit: take },
            ...connectionRequestLookup,

            {
              $project: {
                tags: 1,
                participant: {
                  _id: 1,
                  name: 1,
                  email: 1,
                  status: 1,
                  isAcceptMeetings: 1,
                  createdAt: 1,
                  updatedAt: 1,
                  lastLoginAt: 1,
                  active: 1,
                },
                profile: 1,
                id: { $toString: "$participant._id" },
                _id: 0,
                groupType: "$type",
                groupName: "$translations",
                color: "$color",
                connectionRequests: 1,
                networking: 1,
                isBookmarked: 1,
              },
            },
          ],
          pagination: [
            {
              $group: {
                _id: null,
                total: { $sum: 1 },
              },
            },
            {
              $addFields: {
                hasNextPage: { $lt: [page * take, "$total"] },
                hasPreviousPage: { $gt: [(page - 1) * take, 0] },
                currentPage: page,
              },
            },
            { $project: { _id: 0 } },
          ],
        },
      },
    ];
    // Run the aggregation query
    const result = (await prisma.participantGroup.aggregateRaw({
      pipeline,
    })) as unknown as {
      data: any[];
      pagination: any[];
    }[];

    return {
      data: result?.[0]?.data || [],
      pagination: result?.[0]?.pagination?.[0],
    };
  } catch (error) {
    console.error(error);
    throw new Error("Failed to get participant groups");
  }
};

export const getParticipantsById = async (
  id: string,
  lang: string,
  eventId: string,
  userId?: string,
  companyHint?: { id: string; type: "SPONSOR" | "EXHIBITOR" }
) => {
  try {
    // Step 1: Quick existence check first
    const userExists = await prisma.user.findFirst({
      where: { id },
      select: { id: true },
    });

    if (!userExists) {
      return null;
    }

    // Step 2: Get language ID once (cache this in production)
    const language = await prisma.language.findFirst({
      where: { code: lang },
      select: { id: true },
    });

    if (!language) {
      throw new Error(`Language not found: ${lang}`);
    }

    // Step 3: Parallel queries with optimized selects
    const participant = await prisma.user.findFirst({
      where: { id },
      include: {
        networking: true,
        tags: true,
        meetings: {
          where: {
            type: MeetingParticipantType.SPEAKER,
          },
          include: {
            meeting: {
              include: {
                sessionType: {
                  include: {
                    translations: {
                      where: {
                        language: {
                          code: lang,
                        },
                      },
                    },
                  },
                },
                topics: true,
                translations: {
                  where: {
                    languageId: language.id,
                  },
                },
              },
            },
          },
        },
        profiles: {
          where: {
            eventId,
          },
          orderBy: {
            createdAt: "desc",
          },
          take: 1,
          include: {
            interests: {
              include: {
                translations: {
                  where: {
                    language: {
                      code: lang,
                    },
                  },
                },
              },
            },
            participantGroups: {
              select: {
                id: true,
                translations: {
                  where: {
                    language: {
                      code: lang,
                    },
                  },
                },
                type: true,
                color: true,
              },
            },
          },
        },
      },
    });

    if (!participant) {
      return null;
    }
    const user = participant;

    const firstNonEmpty = (
      ...vals: (string | null | undefined)[]
    ): string | null => {
      for (const v of vals) {
        if (typeof v === "string" && v.trim()) return v.trim();
      }
      return null;
    };

    // Person profile only — normalize own extraData socials; never overwrite with company fields.
    const profiles = (user.profiles ?? []).map((profile, index) => {
      if (index !== 0) return profile;

      const extra =
        profile.extraData && typeof profile.extraData === "object"
          ? (profile.extraData as Record<string, unknown>)
          : {};
      const sm = profile.socialMedia;

      return {
        ...profile,
        bio: firstNonEmpty(profile.bio, user.about),
        email: firstNonEmpty(profile.email, user.email),
        phoneNumber: firstNonEmpty(profile.phoneNumber, user.phoneNumber),
        image: firstNonEmpty(profile.image, user.image),
        socialMedia: {
          facebook: firstNonEmpty(
            sm?.facebook,
            extra.facebook as string | undefined
          ),
          twitter: firstNonEmpty(
            sm?.twitter,
            extra.twitter as string | undefined,
            extra.x as string | undefined
          ),
          linkedin: firstNonEmpty(
            sm?.linkedin,
            extra.linkedin as string | undefined
          ),
          instagram: firstNonEmpty(
            sm?.instagram,
            extra.instagram as string | undefined
          ),
          website: firstNonEmpty(
            sm?.website,
            extra.website as string | undefined
          ),
        },
        youtube: firstNonEmpty(
          (profile as { youtube?: string }).youtube,
          extra.youtube as string | undefined
        ),
      };
    });

    // Company enrichment only for sponsor/exhibitor roles (skip normal attendees/speakers).
    // companyHint is already self-only (API: session.user.id === participant id).
    // Trust hint id + eventId — sponsors/exhibitors are often on a participant group
    // without teamIds / user.sponsorIds / matching company email (membership OR misses them).
    const sponsorIds = user.sponsorIds ?? [];
    const exhibitorIds = user.exhibitorIds ?? [];
    const groupTypes = new Set(
      (profiles[0]?.participantGroups ?? []).map((g) => g.type)
    );
    const hintType = companyHint?.type;
    const hintId = companyHint?.id?.trim() || "";
    const needsSponsor =
      hintType === "SPONSOR" ||
      sponsorIds.length > 0 ||
      groupTypes.has(ParticipantGroupType.SPONSORS);
    const needsExhibitor =
      hintType === "EXHIBITOR" ||
      exhibitorIds.length > 0 ||
      groupTypes.has(ParticipantGroupType.EXHIBITORS);

    const companySelect = {
      id: true,
      facebook: true,
      linkedin: true,
      x: true,
      instagram: true,
      youtube: true,
      website: true,
      phone: true,
      logo: true,
      tags: true,
    participantGroup: {
      select: {
        id: true,
        type: true,
        color: true,
        translations: {
          where: { languageId: language.id },
          select: { name: true },
        },
      },
    },
      translations: {
        where: { languageId: language.id },
        take: 1,
        select: { bio: true, name: true, tags: true },
      },
    } as const;

    const email = user.email?.trim() || null;

    // Discover path (no hint): require membership proof.
    const sponsorMembershipOr = [
      { teamIds: { has: id } },
      ...(sponsorIds.length ? [{ id: { in: sponsorIds } }] : []),
      ...(email ? [{ email }] : []),
    ];

    const exhibitorMembershipOr = [
      { teamIds: { has: id } },
      ...(exhibitorIds.length ? [{ id: { in: exhibitorIds } }] : []),
      ...(email ? [{ email }] : []),
    ];

    const sponsorWhere =
      hintType === "SPONSOR" && hintId
        ? { id: hintId, eventId }
        : {
            eventId,
            OR: sponsorMembershipOr,
          };

    const exhibitorWhere =
      hintType === "EXHIBITOR" && hintId
        ? { id: hintId, eventId }
        : {
            eventId,
            OR: exhibitorMembershipOr,
          };

    const [sponsor, exhibitor] = await Promise.all([
      needsSponsor
        ? prisma.sponsor.findFirst({
            where: sponsorWhere,
            select: companySelect,
          })
        : Promise.resolve(null),
      needsExhibitor
        ? prisma.exhibitor.findFirst({
            where: exhibitorWhere,
            select: companySelect,
          })
        : Promise.resolve(null),
    ]);

    // Prefer the company type from the login hint when both resolve.
    const selectedCompany =
      hintType === "EXHIBITOR"
        ? {
            company: exhibitor ?? sponsor,
            type: exhibitor ? "EXHIBITOR" : sponsor ? "SPONSOR" : null,
          }
        : hintType === "SPONSOR"
        ? {
            company: sponsor ?? exhibitor,
            type: sponsor ? "SPONSOR" : exhibitor ? "EXHIBITOR" : null,
          }
        : sponsor
        ? { company: sponsor, type: "SPONSOR" }
        : exhibitor
        ? { company: exhibitor, type: "EXHIBITOR" }
        : { company: null, type: null };
    const { company } = selectedCompany;
    const companyTranslation = company?.translations?.[0];
    const companyProfile = company
      ? {
          id: company.id,
          type: selectedCompany.type,
          bio: companyTranslation?.bio ?? null,
          name: companyTranslation?.name ?? null,
          phone: company.phone ?? null,
          logo: company.logo ?? null,
          youtube: company.youtube ?? null,
          tags: pickCompanyTagNames(companyTranslation?.tags, company.tags),
          participantGroup: company.participantGroup,
          socialMedia: {
            facebook: company.facebook ?? null,
            twitter: company.x ?? null,
            linkedin: company.linkedin ?? null,
            instagram: company.instagram ?? null,
            website: company.website ?? null,
          },
        }
      : null;
    const normalizedProfiles =
      companyProfile && profiles[0]
        ? profiles.map((profile, index) =>
            index === 0
              ? {
                  ...profile,
                  phoneNumber: firstNonEmpty(
                    companyProfile.phone,
                    profile.phoneNumber,
                    user.phoneNumber
                  ),
                  companyEntityId: companyProfile.id,
                  companyEntityType: companyProfile.type,
                }
              : profile
          )
        : profiles;

    return {
      ...user,
      profiles: normalizedProfiles,
      companyProfile,
      connectionStatus: userId
        ? await getConnectionStatus(userId, id, eventId)
        : null,
      isBookmarked: userId
        ? await checkBookmarkExists(userId, id, eventId)
        : false,
    };
  } catch (error) {
    console.error("Error in getParticipantsByIdOptimized:", error);
    throw new Error(`Failed to get participant by ID: ${id}`);
  }
};

// Separate optimized functions for better caching and reusability
const getConnectionStatus = async (
  userId: string,
  targetId: string,
  eventId: string
) => {
  const connection = await prisma.connectionRequest.findFirst({
    where: {
      eventId,
      OR: [
        { senderId: userId, receiverId: targetId },
        { senderId: targetId, receiverId: userId },
      ],
      status: { not: ConnectionStatus.REJECTED },
    },
    select: {
      status: true,
    },
    orderBy: {
      createdAt: "desc",
    },
  });

  return connection?.status || null;
};

const checkBookmarkExists = async (
  userId: string,
  entityId: string,
  eventId: string
) => {
  const bookmark = await prisma.bookmark.findFirst({
    where: {
      userId,
      eventId,
      entityId,
      entityModel: EntityModel.PROFILE,
    },
    select: {
      id: true,
    },
  });

  return !!bookmark;
};

export const getParticipantGroupsByEventIdAndType = async (
  eventId: string,
  type: ParticipantGroupType
) => {
  const participantGroups = await prisma.participantGroup.findMany({
    where: { eventId, type },
    include: {
      translations: true,
    },
  });
  return participantGroups;
};

export const updateParticipantGroup = async (
  id: string,
  data: {
    translations?: ParticipantGroupTranslations[];
    type?: ParticipantGroupType;
    color?: string;
  },
  tickets: string[],
  invitations: string[]
) => {
  try {
    const bookableItemIds = tickets.length
      ? await getBookableItemIdsFromTicketIds(tickets)
      : [];
    const participantGroup = await prisma.participantGroup.update({
      where: { id },
      data: {
        ...data,
        translations: data.translations
          ? {
              deleteMany: {},
              create: data.translations,
            }
          : undefined,
        bookableItems: {
          connect: bookableItemIds.map((id) => ({ id })),
        },
        invitations: {
          connect: invitations.map((invitation) => ({ id: invitation })),
        },
      },
    });
    return participantGroup;
  } catch (error) {
    console.error(error);
    throw new Error("Failed to update participant group");
  }
};

export const deleteParticipantGroup = async (id: string) => {
  try {
    return await prisma.$transaction(async (ctx) => {
      await ctx.participantGroup.update({
        where: { id },
        data: {
          invitations: {
            set: [],
          },
          bookableItems: {
            set: [],
          },
        },
      });
      await ctx.participantGroupTask.deleteMany({
        where: { participantGroupId: id },
      });
      await ctx.participantGroup.delete({
        where: { id },
      });
    });
  } catch (error) {
    console.error(error);
    throw new Error("Failed to delete participant group");
  }
};

export const addParticipantsToParticipantGroup = async (
  participantGroupId: string,
  eventId: string,
  profiles: Profile[],
  type: ParticipantGroupType,
  session: User,
  whiteLabelId: string,
  participantImportId?: string
) => {
  const languages = await getLanguages();
  const profilesData = profiles.map(async (profile) => {
    const {
      email,
      name,
      phoneNumber,
      qrReferenceNumber,
      jobTitle,
      company,
      country,
      sector,
      city,
      state,
      birthday,
      gender,
      age,
      bio,
    } = profile;
    const tags = Array.isArray((profile as { tags?: string[] }).tags)
      ? (profile as { tags?: string[] }).tags!
      : [];
    const normalizedEmail = normalizeParticipantImportEmail(email ?? "");
    const eventProfile = await prisma.profile.findFirst({
      where: {
        eventId,
        OR: [{ email: normalizedEmail }, { user: { email: normalizedEmail } }],
      },
      select: { id: true },
    });
    if (eventProfile) {
      throw new Error(
        `Email "${normalizedEmail}" already belongs to a participant in this event`
      );
    }
    if (type === ParticipantGroupType.EXHIBITORS) {
      const response = await createExhibitor(
        {
          name: name || "",
          email: normalizedEmail,
          phone: phoneNumber || "",
          contactName: name || "",
          contactJobTitle: jobTitle || "",
          qrReferenceNumber,
          jobTitle: jobTitle || "",
          company: company || name || "",
          tags,
          bio: bio || "",
          country: country || "",
          sector: sector || "",
          city: city || "",
          state: state || "",
          birthday,
          gender: gender || "",
          age,
          event: {
            connect: {
              id: eventId,
            },
          },
        },
        eventId,
        [
          {
            name: name || "",
            languageId: languages?.[0]?.id ?? "",
            contactName: name || "",
            bio: bio || "",
            tags,
            adminNotes: "",
            description: "",
          },
        ],
        whiteLabelId,
        session,
        participantGroupId,
        undefined,
        participantImportId
      );
      return response;
    }

    if (type === ParticipantGroupType.SPONSORS) {
      const response = await createSponsor(
        {
          contactName: name || "",
          email: normalizedEmail,
          phone: phoneNumber || "",
          contactJobTitle: jobTitle || "",
          qrReferenceNumber,
          company: company || name || "",
          tags,
          country: country || "",
          sector: sector || "",
        },
        eventId,
        [
          {
            name: name || "",
            languageId: languages?.[0]?.id ?? "",
            contactName: name || "",
            bio: bio || "",
            tags,
            adminNotes: "",
            description: "",
          },
        ],
        whiteLabelId,
        participantGroupId,
        session,
        undefined,
        participantImportId
      );
      return response;
    }

    const user = await prisma.user.upsert({
      where: {
        email_accessibleWhiteLabelId: {
          email: normalizedEmail,
          accessibleWhiteLabelId: whiteLabelId,
        },
      },
      update: {},
      create: {
        email: normalizedEmail,
        name: name || "",
        phoneNumber: phoneNumber || undefined,
        events: {
          connect: {
            id: eventId,
          },
        },
        accessibleWhiteLabelId: whiteLabelId,
        participantImportId,
      },
      include: {
        profiles: {
          select: {
            id: true,
          },
          where: {
            event: {
              id: eventId,
            },
          },
          take: 1,
          orderBy: {
            createdAt: "desc",
          },
        },
      },
    });
    const existingProfile = await findExistingProfile(
      normalizedEmail,
      phoneNumber,
      eventId,
      user.id
    );
    const newProfile = await createOrUpdateProfile(
      existingProfile,
      {
        name: name || "",
        email: normalizedEmail,
        phoneNumber: phoneNumber || undefined,
        qrReferenceNumber: qrReferenceNumber || undefined,
        jobTitle: jobTitle || undefined,
        company: company || undefined,
        country: country || undefined,
        city: city || undefined,
        state: state || undefined,
        birthday: birthday || undefined,
        gender: gender || undefined,
        age: age || undefined,
        bio: bio || undefined,
        participantImportId,
      },
      eventId,
      undefined,
      participantGroupId,
      undefined,
      user.id,
      false
    );
    const result = await setupParticipantFeatures(
      newProfile,
      [participantGroupId],
      eventId,
      whiteLabelId,
      user.id,
      participantImportId
    );

    return { ...user, token: result.token };
  });
  return profilesData;
};

export const getParticipantGroupsByOrderId = async (orderId: string) => {
  try {
    const orderItems = await prisma.orderItem.findMany({
      where: { orderId },
      select: {
        ticket: {
          select: {
            bookableItem: {
              select: {
                participantGroups: {
                  select: {
                    id: true,
                  },
                },
              },
            },
          },
        },
      },
    });

    // Get all participant groups from BookableItems
    const participantGroupIds = new Set<string>();

    orderItems.forEach((item) => {
      if (item?.ticket?.bookableItem?.participantGroups) {
        item.ticket.bookableItem.participantGroups.forEach((group) =>
          participantGroupIds.add(group.id)
        );
      }
    });

    return Array.from(participantGroupIds);
  } catch (error) {
    console.error(error);
    throw new Error("Failed to get participant groups by order ID");
  }
};

export const isParticipantInvitationAccepted = async (
  entityId: string,
  email: string,
  entityType: UserRoleAssignmentEntityType
) => {
  let entityExisted = false;
  switch (entityType) {
    case UserRoleAssignmentEntityType.EXHIBITOR: {
      const exhibitor = await prisma.exhibitor.findUnique({
        where: {
          id: entityId,
        },
      });
      entityExisted = !!exhibitor;
      break;
    }
    case UserRoleAssignmentEntityType.SPONSOR: {
      const sponsor = await prisma.sponsor.findUnique({
        where: {
          id: entityId,
        },
      });
      entityExisted = !!sponsor;
      break;
    }
    default: {
      const user = await prisma.user.findUnique({
        where: {
          id: entityId,
        },
      });
      entityExisted = !!user;
      break;
    }
  }
  const invitation = await prisma.participantInvitation.findUnique({
    where: {
      entityId_entityType_email: {
        entityId,
        entityType: ParticipantType.EXHIBITOR,
        email,
      },
    },
  });
  return {
    isEntityExisted: entityExisted,
    accepted: invitation?.status === InvitationStatus.ACCEPTED,
  };
};
export const getParticipantInvitationToken = async (
  entityId: string,
  email: string,
  entityType?: ParticipantType
) => {
  try {
    if (!entityId || !email) return undefined;
    const normalizedEmail = email.toLocaleLowerCase();

    const exact = await prisma.participantInvitation.findFirst({
      where: {
        entityId,
        email: normalizedEmail,
        ...(entityType && { entityType }),
      },
      orderBy: { updatedAt: "desc" },
    });
    if (exact?.token) return exact.token;

    // Legacy rows may store mixed-case emails.
    const byEmailInsensitive = await prisma.participantInvitation.findFirst({
      where: {
        entityId,
        email: { equals: email, mode: "insensitive" },
        ...(entityType && { entityType }),
      },
      orderBy: { updatedAt: "desc" },
    });
    if (byEmailInsensitive?.token) return byEmailInsensitive.token;

    // Profile.email vs User.email / USER vs VISITOR type often diverge —
    // retry without entityType before giving up on this email.
    if (entityType) {
      const byEmail = await prisma.participantInvitation.findFirst({
        where: {
          entityId,
          OR: [
            { email: normalizedEmail },
            { email: { equals: email, mode: "insensitive" } },
          ],
        },
        orderBy: { updatedAt: "desc" },
      });
      if (byEmail?.token) return byEmail.token;
    }

    // ponytail: no entityId-only fallback — that can embed another invitee's JWT
    return undefined;
  } catch (error) {
    throw new Error("Failed to get participant invitation token");
  }
};

/**
 * Upsert invitation for entityId+email+type and always re-sign JWT for that email.
 * Uses @@unique([entityId, entityType, email]) — indexed, no COLLSCAN.
 * Re-sign avoids stale payload.email after invitation email was updated in place.
 *
 * Mongo may miss `mode: "insensitive"` finds, so always prefer the normalized
 * unique key, then catch P2002 races instead of failing email side-effects.
 */
export const ensureParticipantInvitationToken = async (
  entityId: string,
  email: string,
  entityType: ParticipantType,
  userId?: string
) => {
  if (!entityId || !email) return undefined;
  const normalizedEmail = email.toLocaleLowerCase();

  const secret = process.env.SECRET_HASH_PASSWORD;
  if (!secret) {
    return undefined;
  }

  const token = jwt.sign(
    { entityId, email: normalizedEmail, entityType },
    secret
  );

  const uniqueWhere = {
    entityId_entityType_email: {
      entityId,
      entityType,
      email: normalizedEmail,
    },
  };

  const refresh = async (id: string, emailToStore = normalizedEmail) => {
    await prisma.participantInvitation.update({
      where: { id },
      data: {
        token,
        email: emailToStore,
        ...(userId ? { userId } : {}),
      },
    });
    return token;
  };

  // Indexed unique key first (works even when insensitive find is a no-op on Mongo).
  const byNormalized = await prisma.participantInvitation.findUnique({
    where: uniqueWhere,
  });
  if (byNormalized) return refresh(byNormalized.id);

  // Legacy mixed-case row for the same entity+type.
  const legacy = await prisma.participantInvitation.findFirst({
    where: {
      entityId,
      entityType,
      email: { equals: email, mode: "insensitive" },
    },
    orderBy: { updatedAt: "desc" },
  });
  if (legacy) {
    try {
      return await refresh(legacy.id);
    } catch (error) {
      // Normalizing email collided with an existing lowercase row — keep that one.
      if (
        error instanceof Prisma.PrismaClientKnownRequestError &&
        error.code === "P2002"
      ) {
        const collision = await prisma.participantInvitation.findUnique({
          where: uniqueWhere,
        });
        if (collision) {
          await prisma.participantInvitation.update({
            where: { id: collision.id },
            data: {
              token,
              status:
                legacy.status === InvitationStatus.ACCEPTED ||
                collision.status === InvitationStatus.ACCEPTED
                  ? InvitationStatus.ACCEPTED
                  : collision.status,
              isOwner: Boolean(legacy.isOwner || collision.isOwner),
              userId: userId ?? legacy.userId ?? collision.userId ?? undefined,
            },
          });
          if (legacy.id !== collision.id) {
            await prisma.participantInvitation.delete({
              where: { id: legacy.id },
            });
          }
          return token;
        }
      }
      throw error;
    }
  }

  try {
    await prisma.participantInvitation.create({
      data: {
        entityId,
        entityType,
        email: normalizedEmail,
        token,
        status: InvitationStatus.PENDING,
        isOwner: true,
        ...(userId ? { userId } : {}),
      },
    });
  } catch (error) {
    // Concurrent ensure / missed find — refresh the winning row.
    if (
      error instanceof Prisma.PrismaClientKnownRequestError &&
      error.code === "P2002"
    ) {
      await prisma.participantInvitation.update({
        where: uniqueWhere,
        data: {
          token,
          ...(userId ? { userId } : {}),
        },
      });
      return token;
    }
    throw error;
  }

  return token;
};

export async function retargetParticipantInvitationEmail(
  entityIds: string[],
  previousEmails: string | string[],
  nextEmail: string,
  userId?: string,
  db: Prisma.TransactionClient | typeof prisma = prisma
) {
  const uniqueIds = [...new Set(entityIds.filter(Boolean))];
  const previous = (
    Array.isArray(previousEmails) ? previousEmails : [previousEmails]
  )
    .map((email) => email.trim().toLowerCase())
    .filter(Boolean);
  const next = nextEmail.trim().toLowerCase();
  const stalePrevious = previous.filter((email) => email !== next);
  if (!uniqueIds.length || !stalePrevious.length || !next) return;

  const secret = process.env.SECRET_HASH_PASSWORD;
  if (!secret) return;

  const select = {
    id: true,
    entityId: true,
    entityType: true,
    email: true,
  } as const;
  // Indexed lookups only: @@unique([entityId, entityType, email]) and @@index([userId]).
  // Mixed-case emails are filtered in memory — mode:"insensitive" would COLLSCAN.
  const [byEntity, byUser] = await Promise.all([
    db.participantInvitation.findMany({
      where: { entityId: { in: uniqueIds } },
      select,
    }),
    userId
      ? db.participantInvitation.findMany({
          where: { userId },
          select,
        })
      : Promise.resolve([]),
  ]);
  const seen = new Set<string>();
  const rows = [...byEntity, ...byUser].filter((row) => {
    if (seen.has(row.id)) return false;
    seen.add(row.id);
    return invitationRowMatchesRetargetEmails(row.email, stalePrevious, next);
  });

  const plan = planInvitationEmailRetarget(rows, next);
  const resign = (entityId: string, entityType: ParticipantType) =>
    jwt.sign({ entityId, email: next, entityType }, secret);

  const rowById = new Map(rows.map((row) => [row.id, row]));

  for (const id of plan.retargetIds) {
    const row = rowById.get(id);
    if (!row) continue;
    const token = resign(row.entityId, row.entityType);
    try {
      await db.participantInvitation.update({
        where: { id },
        data: {
          token,
          email: next,
          ...(userId ? { userId } : {}),
        },
      });
    } catch (error) {
      if (
        error instanceof Prisma.PrismaClientKnownRequestError &&
        error.code === "P2002"
      ) {
        const collision = await db.participantInvitation.findUnique({
          where: {
            entityId_entityType_email: {
              entityId: row.entityId,
              entityType: row.entityType,
              email: next,
            },
          },
        });
        if (collision) {
          await db.participantInvitation.update({
            where: { id: collision.id },
            data: {
              token,
              ...(userId ? { userId } : {}),
            },
          });
          if (collision.id !== id) {
            await db.participantInvitation.delete({ where: { id } });
          }
        }
        continue;
      }
      throw error;
    }
  }

  for (const id of plan.refreshIds) {
    const row = rowById.get(id);
    if (!row) continue;
    await db.participantInvitation.update({
      where: { id },
      data: {
        token: resign(row.entityId, row.entityType),
        email: next,
        ...(userId ? { userId } : {}),
      },
    });
  }

  if (plan.deleteIds.length > 0) {
    await db.participantInvitation.deleteMany({
      where: { id: { in: plan.deleteIds } },
    });
  }
}

/** Login email for exhibitor/sponsor magic links: owner User.email, not company email. */
export const ensureOwnerMagicLinkToken = async (
  entityId: string,
  entityType: ParticipantType,
  fallbackEmail: string
) => {
  const secret = process.env.SECRET_HASH_PASSWORD;
  if (!entityId || !secret) return undefined;

  const ownerInvites = await prisma.participantInvitation.findMany({
    where: { entityId, entityType, isOwner: true },
    select: {
      id: true,
      email: true,
      userId: true,
      status: true,
      user: { select: { email: true } },
    },
    orderBy: { updatedAt: "desc" },
  });

  const ownerInvite = pickLinkedOwnerInvitation(ownerInvites);
  const loginEmail = (
    ownerInvite?.user?.email ||
    ownerInvite?.email ||
    fallbackEmail
  )?.toLocaleLowerCase();
  const userId = ownerInvite?.userId ?? undefined;

  if (!loginEmail) return undefined;
  if (!ownerInvite) {
    return ensureParticipantInvitationToken(
      entityId,
      loginEmail,
      entityType,
      userId
    );
  }

  const token = jwt.sign({ entityId, email: loginEmail, entityType }, secret);

  try {
    await prisma.participantInvitation.update({
      where: { id: ownerInvite.id },
      data: {
        token,
        email: loginEmail,
        ...(userId ? { userId } : {}),
      },
    });
    return token;
  } catch (error) {
    if (
      error instanceof Prisma.PrismaClientKnownRequestError &&
      error.code === "P2002"
    ) {
      const collision = await prisma.participantInvitation.findUnique({
        where: {
          entityId_entityType_email: {
            entityId,
            entityType,
            email: loginEmail,
          },
        },
      });
      if (collision) {
        await prisma.participantInvitation.update({
          where: { id: collision.id },
          data: {
            token,
            isOwner: true,
            userId: userId ?? collision.userId ?? undefined,
            status:
              ownerInvite.status === InvitationStatus.ACCEPTED ||
              collision.status === InvitationStatus.ACCEPTED
                ? InvitationStatus.ACCEPTED
                : collision.status,
          },
        });
        if (collision.id !== ownerInvite.id) {
          await prisma.participantInvitation.delete({
            where: { id: ownerInvite.id },
          });
        }
        return token;
      }
    }
    throw error;
  }
};

export const getUserFromInvitationToken = async (token: string) => {
  try {
    const invitation = await prisma.participantInvitation.findUnique({
      where: {
        token,
      },
      include: {
        user: true,
      },
    });

    return invitation?.user as User | null;
  } catch (error) {
    console.error(error);
    throw new Error("Failed to get user from invitation token");
  }
};
export const isUserExistInEvent = async (
  userEmail: string,
  eventId: string
) => {
  const user = await prisma.user.findFirst({
    where: {
      email: userEmail.toLocaleLowerCase(),
      profiles: {
        some: {
          event: {
            slug: eventId,
          },
        },
      },
    },
  });

  return !!user;
};
export const removeParticipantFromGroup = async (
  participantId: string,
  groupId: string,
  eventId: string
) => {
  try {
    const group = await prisma.participantGroup.findUnique({
      where: { id: groupId },
      select: { type: true },
    });

    if (!group) {
      throw new Error("Participant group not found");
    }

    if (group.type === ParticipantGroupType.EXHIBITORS) {
      const exhibitor = await prisma.exhibitor.findFirst({
        where: {
          id: participantId,
          eventId,
          participantGroupId: groupId,
        },
        select: { id: true },
      });
      if (!exhibitor) {
        throw new Error("Participant not found in group");
      }
      await deleteExhibitor(participantId);
      return;
    }

    if (group.type === ParticipantGroupType.SPONSORS) {
      const sponsor = await prisma.sponsor.findFirst({
        where: {
          id: participantId,
          eventId,
          participantGroupId: groupId,
        },
        select: { id: true },
      });
      if (!sponsor) {
        throw new Error("Participant not found in group");
      }
      await deleteSponsor(participantId);
      return;
    }

    await prisma.$transaction(async (tx) => {
      await tx.formEntity.deleteMany({
        where: { entityID: participantId },
      });

      await tx.profile.deleteMany({
        where: {
          id: participantId,
          eventId: eventId,
        },
      });

      await tx.participantGroup.update({
        where: {
          id: groupId,
        },
        data: {
          participants: {
            disconnect: {
              id: participantId,
            },
          },
        },
      });
    });
  } catch (error) {
    console.error(error);
    throw new Error("Failed to remove participant from group");
  }
};
